import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../services/api';

const STEPS = [
  { id: 1, title: 'Personal Information' },
  { id: 2, title: 'Home Address' },
  { id: 3, title: 'Business Details' },
  { id: 4, title: 'Collateral' },
  { id: 5, title: 'KYC Documents' },
  { id: 6, title: 'Registration Fee' },
  { id: 7, title: 'Contracts & Signed Docs' },
  { id: 8, title: 'Review & Submit' },
];

const REQUIRED_KYC_DOCS = [
  { key: 'id-front', label: 'National ID (Front)', required: true },
  { key: 'id-back', label: 'National ID (Back)', required: true },
  { key: 'passport', label: 'Passport Photo', required: true },
  { key: 'other', label: 'Other Documents', required: false }
];

const INTERACTION_TYPES = [
  { value: 'note', label: '📝 Note', color: '#6366f1' },
  { value: 'call', label: '📞 Call', color: '#059669' },
  { value: 'visit', label: '🏠 Visit', color: '#d97706' },
  { value: 'sms', label: '💬 SMS', color: '#2563eb' },
  { value: 'whatsapp', label: '📱 WhatsApp', color: '#16a34a' },
  { value: 'email', label: '✉️ Email', color: '#7c3aed' },
  { value: 'other', label: '📌 Other', color: '#6b7280' },
];

const STORAGE_KEY = (leadId) => `onboarding_draft_${leadId}`;

// ═══════════════════════════════════════════════════════════
// STYLE TOKENS
// ═══════════════════════════════════════════════════════════
const T = {
  primary: '#4f46e5',
  primaryLight: '#eef2ff',
  primaryDark: '#3730a3',
  success: '#059669',
  successLight: '#ecfdf5',
  successBorder: '#a7f3d0',
  warning: '#d97706',
  warningLight: '#fffbeb',
  warningBorder: '#fde68a',
  danger: '#dc2626',
  dangerLight: '#fef2f2',
  dangerBorder: '#fecaca',
  bg: '#f1f5f9',
  card: '#ffffff',
  text: '#1e293b',
  textSecondary: '#64748b',
  textMuted: '#94a3b8',
  border: '#e2e8f0',
  borderLight: '#f1f5f9',
  inputBg: '#f8fafc',
  shadowSm: '0 1px 2px rgba(0,0,0,0.05)',
  shadow: '0 1px 3px rgba(0,0,0,0.08), 0 1px 2px rgba(0,0,0,0.04)',
  shadowMd: '0 4px 6px -1px rgba(0,0,0,0.07), 0 2px 4px -2px rgba(0,0,0,0.05)',
  shadowLg: '0 10px 15px -3px rgba(0,0,0,0.08), 0 4px 6px -4px rgba(0,0,0,0.04)',
  radius: 12,
  radiusLg: 16,
  radiusXl: 20,
};

const extractId = (val) => {
  if (!val) return null;
  if (typeof val === 'object' && val._id) return String(val._id);
  if (typeof val === 'string') return val;
  return null;
};

// ─── REUSABLE COMPONENTS ───

const AddButton = ({ onClick, label, disabled = false }) => (
  <button type="button" className="btn btn-sm" onClick={onClick} disabled={disabled}
    style={{
      display: 'inline-flex', alignItems: 'center', gap: 6, padding: '7px 16px',
      background: disabled ? '#e2e8f0' : `linear-gradient(135deg, ${T.success} 0%, #047857 100%)`,
      color: disabled ? T.textMuted : '#fff', border: 'none', borderRadius: 10,
      fontSize: 13, fontWeight: 600, cursor: disabled ? 'not-allowed' : 'pointer',
      transition: 'all 0.2s ease', boxShadow: disabled ? 'none' : `0 2px 8px rgba(5, 150, 105, 0.3)`,
    }}
  >
    <span style={{ fontSize: 16, lineHeight: 1 }}>+</span>{label}
  </button>
);

const SectionHeader = ({ title, onAdd, addLabel, showAdd = true, maxItems, currentCount }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, paddingBottom: 12, borderBottom: `2px solid ${T.border}` }}>
    <h5 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: T.text, letterSpacing: '-0.01em' }}>
      {title}
      {maxItems && <span style={{ fontSize: 12, color: T.textMuted, fontWeight: 500, marginLeft: 8 }}>({currentCount}/{maxItems})</span>}
    </h5>
    {showAdd && <AddButton onClick={onAdd} label={addLabel} disabled={maxItems ? currentCount >= maxItems : false} />}
  </div>
);

const RemoveButton = ({ onClick, tooltip = 'Remove' }) => (
  <button type="button" onClick={onClick} title={tooltip}
    style={{
      display: 'flex', alignItems: 'center', justifyContent: 'center', width: 30, height: 30,
      borderRadius: 8, background: T.dangerLight, border: `1px solid ${T.dangerBorder}`, color: T.danger,
      fontSize: 13, cursor: 'pointer', transition: 'all 0.15s ease', flexShrink: 0, fontWeight: 600,
    }}
  >✕</button>
);

function useDuplicateCheck() {
  const [checks, setChecks] = useState({});
  const timers = useRef({});

  const check = useCallback((field, value, excludeClientId) => {
    if (timers.current[field]) clearTimeout(timers.current[field]);
    if (!value || value.length < 4) {
      setChecks(prev => ({ ...prev, [field]: null }));
      return;
    }
    setChecks(prev => ({ ...prev, [field]: { loading: true, data: null, error: null } }));
    timers.current[field] = setTimeout(async () => {
      try {
        let url = `/clients/check-duplicate?field=${field}&value=${encodeURIComponent(value)}`;
        if (excludeClientId) url += `&excludeClientId=${excludeClientId}`;
        const res = await api.get(url);
        setChecks(prev => ({ ...prev, [field]: { loading: false, data: res.data.data, error: null } }));
      } catch {
        setChecks(prev => ({ ...prev, [field]: { loading: false, data: null, error: 'Check failed' } }));
      }
    }, 600);
  }, []);

  const clearCheck = useCallback((field) => {
    if (timers.current[field]) clearTimeout(timers.current[field]);
    setChecks(prev => ({ ...prev, [field]: null }));
  }, []);

  useEffect(() => {
    return () => {
      Object.values(timers.current).forEach(t => { if (t) clearTimeout(t); });
    };
  }, []);

  return { checks, check, clearCheck };
}

const DuplicateBadge = ({ result, blocking = false, navigateToClient }) => {
  if (!result) return null;

  if (result.loading) {
    return (
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8, marginTop: 8, fontSize: 12,
        color: T.textSecondary, padding: '8px 12px', background: T.inputBg, borderRadius: 10,
        border: `1px solid ${T.border}`,
      }}>
        <span style={{ width: 14, height: 14, border: `2px solid ${T.border}`, borderTopColor: T.primary, borderRadius: '50%', animation: 'spin 0.8s linear infinite', display: 'inline-block' }} />
        Checking database...
      </div>
    );
  }

  if (result.error) {
    return (
      <p style={{ fontSize: 11, color: T.textMuted, marginTop: 6, padding: '6px 10px' }}>
        ⚠️ Could not verify — will check again on submission
      </p>
    );
  }

  if (!result.data.isDuplicate) {
    return (
      <div style={{
        display: 'flex', alignItems: 'center', gap: 6, marginTop: 8, fontSize: 12,
        color: T.success, padding: '8px 12px', background: T.successLight, borderRadius: 10,
        border: `1px solid ${T.successBorder}`, fontWeight: 500,
      }}>
        ✅ No existing record found with this {result.data.field}
      </div>
    );
  }

  const { clientCount, leadCount, totalCount, clientMatches } = result.data;

  if (blocking && clientCount > 0) {
    return (
      <div style={{
        marginTop: 10, padding: 16, borderRadius: T.radius,
        border: `2px solid ${T.dangerBorder}`, background: T.dangerLight,
      }}>
        <div style={{
          display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12,
          padding: '10px 14px', background: T.danger, borderRadius: 10, color: '#fff',
        }}>
          <span style={{ fontSize: 22 }}>🚫</span>
          <div>
            <p style={{ margin: 0, fontSize: 14, fontWeight: 700 }}>A client with this National ID already exists!</p>
            <p style={{ margin: '3px 0 0', fontSize: 11, opacity: 0.9 }}>You cannot proceed with onboarding. Each National ID must be unique.</p>
          </div>
        </div>
        {clientMatches?.length > 0 && clientMatches.map((m, i) => (
          <div key={i} style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            padding: '12px 14px', background: '#fff', borderRadius: 10, marginBottom: 8,
            border: `1px solid ${T.dangerBorder}`, boxShadow: T.shadowSm,
          }}>
            <div>
              <p style={{ margin: 0, fontSize: 13, fontWeight: 600, color: '#991b1b' }}>{m.firstName} {m.lastName}</p>
              <p style={{ margin: '3px 0 0', fontSize: 11, color: '#7f1d1d' }}>Client No: {m.clientNumber} • Phone: {m.phone}</p>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{
                padding: '4px 10px', borderRadius: 6, fontSize: 11, fontWeight: 600,
                background: m.status === 'approved' ? T.successLight : m.status === 'rejected' ? T.dangerLight : T.warningLight,
                color: m.status === 'approved' ? T.success : m.status === 'rejected' ? T.danger : T.warning,
              }}>{m.status}</span>
              {navigateToClient && m._id && (
                <button type="button" onClick={() => navigateToClient(m._id)} style={{
                  padding: '5px 12px', borderRadius: 8, fontSize: 11, fontWeight: 600,
                  background: T.primary, color: '#fff', border: 'none', cursor: 'pointer',
                }}>👁️ View</button>
              )}
            </div>
          </div>
        ))}
        <div style={{
          marginTop: 10, padding: '10px 14px', background: T.warningLight, borderRadius: 10,
          border: `1px solid ${T.warningBorder}`, fontSize: 12, color: '#92400e',
        }}>
          <strong>What to do:</strong> The person associated with this National ID already has a client record. If you believe this is an error, contact your manager.
        </div>
      </div>
    );
  }

  return (
    <div style={{ marginTop: 8, padding: 10, borderRadius: 10, border: `1px solid ${T.dangerBorder}`, background: T.dangerLight, fontSize: 11 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 4, color: T.danger, fontWeight: 600, marginBottom: 4 }}>
        ⚠️ Found {totalCount} match{totalCount !== 1 ? 'es' : ''} ({clientCount} client{clientCount !== 1 ? 's' : ''}, {leadCount} lead{leadCount !== 1 ? 's' : ''})
      </div>
      {clientMatches?.length > 0 && (
        <div style={{ marginTop: 4 }}>
          {clientMatches.map((m, i) => (
            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0', color: '#7f1d1d' }}>
              <span>{m.firstName} {m.lastName} — {m.clientNumber}</span>
              <span style={{
                padding: '2px 8px', borderRadius: 4, fontSize: 10, fontWeight: 600,
                background: m.status === 'approved' ? T.successLight : T.dangerLight,
                color: m.status === 'approved' ? T.success : T.danger,
              }}>{m.status}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const InteractionModal = ({ isOpen, onClose, onSave, stepId, stepTitle, loading }) => {
  const [type, setType] = useState('note');
  const [content, setContent] = useState('');
  useEffect(() => { if (isOpen) { setType('note'); setContent(''); } }, [isOpen]);
  if (!isOpen) return null;
  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(15,23,42,0.6)', backdropFilter: 'blur(6px)' }} onClick={onClose}>
      <div style={{ background: T.card, borderRadius: T.radiusXl, padding: 28, width: '90%', maxWidth: 480, boxShadow: '0 25px 60px rgba(0,0,0,0.2)' }} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <h4 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: T.text }}>📝 Add Note</h4>
          <button onClick={onClose} style={{ width: 32, height: 32, borderRadius: 8, border: 'none', background: T.inputBg, cursor: 'pointer', fontSize: 16, color: T.textSecondary, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✕</button>
        </div>
        <div style={{ marginBottom: 14 }}>
          <label style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 8, color: T.text }}>Type</label>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {INTERACTION_TYPES.map(t => (
              <button key={t.value} type="button" onClick={() => setType(t.value)} style={{
                padding: '6px 14px', borderRadius: 20, fontSize: 12, fontWeight: 500,
                border: `1.5px solid ${type === t.value ? t.color : T.border}`,
                background: type === t.value ? t.color + '12' : T.card,
                color: type === t.value ? t.color : T.textSecondary, cursor: 'pointer',
                transition: 'all 0.15s ease',
              }}>{t.label}</button>
            ))}
          </div>
        </div>
        <div style={{ marginBottom: 20 }}>
          <label style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 8, color: T.text }}>Note *</label>
          <textarea value={content} onChange={e => setContent(e.target.value)} placeholder="What happened?" rows={4} style={{
            width: '100%', padding: '12px 14px', borderRadius: 10, border: `1.5px solid ${T.border}`,
            fontSize: 13, resize: 'vertical', fontFamily: 'inherit', outline: 'none',
            background: T.inputBg, color: T.text, transition: 'border-color 0.15s',
          }} />
        </div>
        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button onClick={onClose} style={{
            padding: '9px 22px', borderRadius: 10, border: `1px solid ${T.border}`, background: T.card,
            cursor: 'pointer', fontSize: 13, color: T.textSecondary, fontWeight: 500,
          }}>Cancel</button>
          <button onClick={() => { if (content.trim()) { onSave({ type, content: content.trim(), step: stepId, stepTitle }); setContent(''); onClose(); } }} disabled={!content.trim() || loading} style={{
            padding: '9px 22px', borderRadius: 10, border: 'none',
            background: content.trim() ? T.primary : '#c7d2fe', color: '#fff',
            cursor: content.trim() ? 'pointer' : 'not-allowed', fontSize: 13, fontWeight: 600,
          }}>{loading ? 'Saving...' : 'Save'}</button>
        </div>
      </div>
    </div>
  );
};

const InteractionPanel = ({ isOpen, onClose, interactions, loading }) => (
  <>
    {isOpen && <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 900, background: 'rgba(15,23,42,0.3)' }} />}
    <div style={{
      position: 'fixed', top: 0, right: isOpen ? 0 : -420, zIndex: 950, width: 400, maxWidth: '90vw', height: '100vh',
      background: T.card, boxShadow: isOpen ? '-8px 0 40px rgba(0,0,0,0.12)' : 'none',
      transition: 'right 0.3s cubic-bezier(0.4,0,0.2,1)', display: 'flex', flexDirection: 'column',
    }}>
      <div style={{ padding: '18px 22px', borderBottom: `1px solid ${T.border}`, display: 'flex', justifyContent: 'space-between', background: T.inputBg }}>
        <div><h4 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: T.text }}>📋 Interactions</h4><p style={{ margin: '3px 0 0', fontSize: 12, color: T.textMuted }}>{interactions.length} total</p></div>
        <button onClick={onClose} style={{ width: 34, height: 34, borderRadius: 10, border: 'none', background: T.border, cursor: 'pointer', fontSize: 15, color: T.textSecondary, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✕</button>
      </div>
      <div style={{ flex: 1, overflowY: 'auto', padding: '14px 22px' }}>
        {interactions.map((item, i) => {
          const typeInfo = INTERACTION_TYPES.find(t => t.value === item.type) || INTERACTION_TYPES[6];
          return (
            <div key={i} style={{
              padding: 14, borderRadius: T.radius, marginBottom: 10,
              border: `1px solid ${T.border}`, borderLeft: `4px solid ${typeInfo.color}`,
              background: T.card, boxShadow: T.shadowSm,
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                <span style={{ fontSize: 11, fontWeight: 600, padding: '3px 10px', borderRadius: 12, background: typeInfo.color + '12', color: typeInfo.color }}>{typeInfo.label}</span>
                <span style={{ fontSize: 10, color: T.textMuted }}>{new Date(item.createdAt).toLocaleDateString()}</span>
              </div>
              <p style={{ margin: 0, fontSize: 13, lineHeight: 1.6, whiteSpace: 'pre-wrap', color: T.text }}>{item.content}</p>
              {item.recordedBy && <p style={{ margin: '8px 0 0', fontSize: 11, color: T.textMuted }}>— {item.recordedBy.firstName} {item.recordedBy.lastName}</p>}
            </div>
          );
        })}
        {interactions.length === 0 && <p style={{ textAlign: 'center', color: T.textMuted, padding: 40, fontSize: 13 }}>No interactions yet.</p>}
      </div>
    </div>
  </>
);

const PhotoGrid = ({ photos, field, updatePhotoDesc, removePhoto }) => (
  <div style={{ maxHeight: '300px', overflowY: 'auto', marginTop: 12 }}>
    {photos.map((p, i) => (
      <div key={i} style={{
        display: 'flex', gap: 14, alignItems: 'flex-start', marginBottom: 12,
        background: T.inputBg, padding: 14, borderRadius: T.radius, border: `1px solid ${T.border}`,
      }}>
        <img src={p.image} alt="" style={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 10, border: `1px solid ${T.border}`, flexShrink: 0 }} />
        <div style={{ flex: 1 }}>
          <textarea className="form-control" rows={2} style={{ fontSize: 12, padding: '8px 12px', borderRadius: 8, border: `1px solid ${T.border}`, background: T.card }} placeholder="Description..." value={p.description} onChange={e => updatePhotoDesc(field, i, e.target.value)} />
        </div>
        <RemoveButton onClick={() => removePhoto(field, i)} />
      </div>
    ))}
    {photos.length === 0 && <p style={{ textAlign: 'center', color: T.textMuted, fontSize: 13, padding: 24 }}>No photos added.</p>}
  </div>
);

// ─── INPUT HELPERS ───

const inputStyle = {
  width: '100%', padding: '10px 14px', borderRadius: 10, border: `1.5px solid ${T.border}`,
  fontSize: 14, background: T.inputBg, color: T.text, outline: 'none',
  transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
  fontFamily: 'inherit',
};

const labelStyle = {
  display: 'block', fontSize: 13, fontWeight: 600, color: T.text, marginBottom: 6,
};

const selectStyle = {
  ...inputStyle, cursor: 'pointer', appearance: 'none',
  backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%2364748b' d='M6 8L1 3h10z'/%3E%3C/svg%3E")`,
  backgroundRepeat: 'no-repeat', backgroundPosition: 'right 12px center',
  paddingRight: 32,
};

// ─── STEP INDICATOR ───

const StepIndicator = ({ steps, currentStep, completedSteps }) => (
  <div style={{
    background: T.card, borderRadius: T.radiusXl, padding: '20px 24px', marginBottom: 24,
    boxShadow: T.shadowMd, border: `1px solid ${T.border}`,
  }}>
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'relative' }}>
      {/* Progress bar background */}
      <div style={{
        position: 'absolute', top: '50%', left: 28, right: 28, height: 3,
        background: T.border, borderRadius: 2, transform: 'translateY(-50%)', zIndex: 0,
      }}>
        <div style={{
          height: '100%', borderRadius: 2,
          background: `linear-gradient(90deg, ${T.primary} 0%, ${T.success} 100%)`,
          width: `${((Array.from(completedSteps).length) / (steps.length - 1)) * 100}%`,
          transition: 'width 0.5s ease',
        }} />
      </div>
      {steps.map((step, i) => {
        const isCompleted = completedSteps.has(step.id);
        const isCurrent = step.id === currentStep;
        const isFuture = !isCompleted && !isCurrent;
        return (
          <div key={step.id} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', zIndex: 1, flex: 1, position: 'relative' }}>
            <div style={{
              width: isCurrent ? 40 : 34, height: isCurrent ? 40 : 34,
              borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: isCurrent ? 15 : 13, fontWeight: 700,
              background: isCompleted
                ? `linear-gradient(135deg, ${T.success} 0%, #047857 100%)`
                : isCurrent
                  ? `linear-gradient(135deg, ${T.primary} 0%, ${T.primaryDark} 100%)`
                  : T.card,
              color: isCompleted || isCurrent ? '#fff' : T.textMuted,
              border: isFuture ? `2px solid ${T.border}` : 'none',
              boxShadow: isCurrent ? `0 0 0 4px ${T.primary}25, 0 4px 12px ${T.primary}30` : isCompleted ? `0 2px 8px ${T.success}30` : 'none',
              transition: 'all 0.3s ease',
            }}>
              {isCompleted ? '✓' : step.id}
            </div>
            <p style={{
              margin: '8px 0 0', fontSize: 10, fontWeight: isCurrent ? 700 : isCompleted ? 600 : 400,
              color: isCurrent ? T.primary : isCompleted ? T.success : T.textMuted,
              textAlign: 'center', maxWidth: 80, lineHeight: 1.3,
            }}>
              {step.title}
            </p>
          </div>
        );
      })}
    </div>
  </div>
);

// ═══════════════════════════════════════════════════════════════
// FIX 1: Fetch already-uploaded KYC docs from server
// Matches route: router.get('/documents/file/:docId', ...)
// ═══════════════════════════════════════════════════════════════
const fetchExistingKycDocs = async (cid) => {
  if (!cid) return [];
  try {
    const res = await api.get(`/clients/${cid}/documents`);
    const docs = res.data?.data;
    if (!Array.isArray(docs)) return [];
    return docs.map(doc => ({
      documentType: doc.documentType,
      file: null,
      preview: doc._id ? `/clients/documents/file/${doc._id}` : null,
      description: doc.notes || '',
      uploaded: true,
      serverId: doc._id,
    }));
  } catch {
    return [];
  }
};

// ─── MAIN COMPONENT ───

export default function ClientOnboarding() {
  const { leadId } = useParams();
  const navigate = useNavigate();
  const fileInputRefs = useRef({});
  const pollIntervalRef = useRef(null);
  const autoSaveTimerRef = useRef(null);

  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [lead, setLead] = useState(null);
  const [clientId, setClientId] = useState(null);
  const [error, setError] = useState('');
  const [completedSteps, setCompletedSteps] = useState(new Set());
  const [stepMessage, setStepMessage] = useState('');
  const [lastSaved, setLastSaved] = useState(null);
  const [draftLoaded, setDraftLoaded] = useState(false);

  const [interactions, setInteractions] = useState([]);
  const [interactionsLoading, setInteractionsLoading] = useState(false);
  const [interactionModalOpen, setInteractionModalOpen] = useState(false);
  const [interactionPanelOpen, setInteractionPanelOpen] = useState(false);
  const [savingInteraction, setSavingInteraction] = useState(false);

  const { checks, check, clearCheck } = useDuplicateCheck();
  const [refChecks, setRefChecks] = useState({});
  const [manualPaymentInfo, setManualPaymentInfo] = useState({ paybillNumber: '', accountReference: '', amount: 700 });
  const refTimers = useRef({});

  const isNationalIdBlocked = checks.nationalId?.data?.isDuplicate === true &&
    checks.nationalId?.data?.clientCount > 0;

  const emptyForm = {
    nationalId: '', dateOfBirth: '', gender: '', occupation: '',
    employer: '', monthlyIncome: '', maritalStatus: '',
    references: [
      { name: '', phone: '', relationship: '', idNumber: '' },
      { name: '', phone: '', relationship: '', idNumber: '' },
    ],
    homeAddress: { street: '', city: '', county: '', postalCode: '' },
    homeGps: null, homePhotos: [], homeDescription: '',
    businessName: '', businessType: '', businessSector: '', businessAddress: '',
    businessGps: null, businessPhotos: [], businessDescription: '',
    collateral: [{ type: '', description: '', estimatedValue: '', image: null }],
    kycDocuments: [],
    paymentPhone: '', paymentStatus: '', paymentReceipt: '',
    paymentMethod: 'stk', paymentCode: '', paymentVerificationMessage: '',
    contracts: [],
    loanOfficerId: '', collectionOfficerId: '',
  };

  const [form, setForm] = useState(emptyForm);
  const [availableOfficers, setAvailableOfficers] = useState([]);

  useEffect(() => {
    api.get('/users?isActive=true&limit=500').then(res => setAvailableOfficers(res.data.data || [])).catch(() => setAvailableOfficers([]));
  }, []);

  // Load the server-configured Paybill and the client's National ID used as the Option 2 Account Number.
  useEffect(() => {
    const cid = extractId(clientId);
    if (!cid) return;

    let cancelled = false;
    api.get(`/payments/onboarding-fee-status/${cid}`)
      .then(res => {
        if (cancelled) return;
        const data = res.data?.data || {};
        setManualPaymentInfo({
          paybillNumber: data.paybillNumber || '',
          accountReference: data.manualAccountReference || '',
          amount: Number(data.manualAmount || 700),
        });
      })
      .catch(() => {
        if (!cancelled) {
          setManualPaymentInfo(prev => ({
            ...prev,
            accountReference: prev.accountReference || '',
          }));
        }
      });

    return () => { cancelled = true; };
  }, [clientId]);

  useEffect(() => {
    return () => {
      if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
      if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
      Object.values(refTimers.current).forEach(t => { if (t) clearTimeout(t); });
    };
  }, []);

  // ─── LOAD DATA ───
  useEffect(() => {
    if (!leadId) return navigate('/leads');
    const loadData = async () => {
      try {
        const leadRes = await api.get(`/leads/${leadId}`);
        const l = leadRes.data.data;
        setLead(l);

        let serverDraft = null;
        try {
          const draftRes = await api.get(`/clients/draft/${leadId}`);
          serverDraft = draftRes.data.data;
        } catch {}

        if (serverDraft) {
          const draftClientId = extractId(serverDraft.clientId);
          setClientId(draftClientId);
          setCurrentStep(serverDraft.currentStep || 1);
          // FIX 2: If payment was already paid, ensure step 6 is marked complete
          const completed = new Set(serverDraft.completedSteps || []);
          if (serverDraft.formData?.paymentStatus === 'paid') completed.add(6);
          setCompletedSteps(completed);
          const savedForm = serverDraft.formData || {};

          if (draftClientId) {
            try {
              const clientRes = await api.get(`/clients/${draftClientId}`);
              const sc = clientRes.data.data;
              setForm(prev => ({
                ...prev, ...savedForm,
                homePhotos: sc.homePhotos?.length ? sc.homePhotos : (savedForm.homePhotos || []),
                businessPhotos: sc.businessPhotos?.length ? sc.businessPhotos : (savedForm.businessPhotos || []),
                collateral: sc.collateral?.length ? sc.collateral : (savedForm.collateral || [{ type: '', description: '', estimatedValue: '', image: null }]),
                paymentStatus: savedForm.paymentStatus || 'pending',
                homeGps: savedForm.homeGps || sc.gpsLocation || null,
                businessGps: savedForm.businessGps || sc.businessGpsLocation || null,
                homeAddress: savedForm.homeAddress || { street: sc.address?.street || '', city: sc.address?.city || '', county: sc.address?.state || '', postalCode: sc.address?.postalCode || '' },
                homeDescription: savedForm.homeDescription || sc.homeDescription || '',
                businessName: savedForm.businessName || sc.businessName || '',
                businessType: savedForm.businessType || sc.businessType || '',
                businessSector: savedForm.businessSector || sc.businessSector || '',
                businessAddress: savedForm.businessAddress || sc.businessAddress || '',
                businessDescription: savedForm.businessDescription || sc.businessDescription || '',
                // FIX 1: Keep kycDocuments from savedForm (preserves uploaded flags)
                kycDocuments: savedForm.kycDocuments || [],
                contracts: savedForm.contracts || [],
              }));
              // FIX 1: Fetch existing KYC docs from server and merge
              const serverDocs = await fetchExistingKycDocs(draftClientId);
              if (serverDocs.length > 0) {
                setForm(prev => {
                  const merged = [...prev.kycDocuments];
                  for (const sDoc of serverDocs) {
                    const existingIdx = merged.findIndex(d => d.documentType === sDoc.documentType);
                    if (existingIdx >= 0) {
                      if (!merged[existingIdx].preview && sDoc.preview) {
                        merged[existingIdx] = { ...merged[existingIdx], preview: sDoc.preview, uploaded: true, serverId: sDoc.serverId };
                      } else {
                        merged[existingIdx] = { ...merged[existingIdx], uploaded: true, serverId: sDoc.serverId };
                      }
                    } else {
                      merged.push(sDoc);
                    }
                  }
                  return { ...prev, kycDocuments: merged };
                });
              }
            } catch { setForm(prev => ({ ...prev, ...savedForm })); }
          } else {
            setForm(prev => ({ ...prev, paymentPhone: l.phone, ...savedForm }));
          }
          if (serverDraft.updatedAt) setLastSaved(new Date(serverDraft.updatedAt));
        } else {
          if (l.status === 'converted' && l.convertedToClient) {
            try {
              const existingClientId = extractId(l.convertedToClient);
              const clientRes = await api.get(`/clients/${existingClientId}`);
              const sc = clientRes.data.data;
              setClientId(existingClientId);
              setForm(prev => ({
                ...prev,
                paymentPhone: sc.phone || l.phone,
                nationalId: sc.nationalId || '',
                dateOfBirth: sc.dateOfBirth ? new Date(sc.dateOfBirth).toISOString().split('T')[0] : '',
                gender: sc.gender || '',
                occupation: sc.occupation || '',
                employer: sc.employer || '',
                monthlyIncome: sc.monthlyIncome || '',
                maritalStatus: sc.maritalStatus || '',
                references: prev.references,
                homeAddress: { street: sc.address?.street || '', city: sc.address?.city || '', county: sc.address?.state || '', postalCode: sc.address?.postalCode || '' },
                homeGps: sc.gpsLocation || null,
                homePhotos: sc.homePhotos || [],
                homeDescription: sc.homeDescription || sc.address?.description || '',
                businessName: sc.businessName || '',
                businessType: sc.businessType || '',
                businessSector: sc.businessSector || '',
                businessAddress: sc.businessAddress || '',
                businessGps: sc.businessGpsLocation || null,
                businessPhotos: sc.businessPhotos || [],
                businessDescription: sc.businessDescription || '',
                collateral: sc.collateral?.length ? sc.collateral : [{ type: '', description: '', estimatedValue: '', image: null }],
                paymentStatus: 'pending',
                paymentMethod: 'stk', paymentCode: '', paymentVerificationMessage: '',
              }));
              const completed = new Set();
              if (sc.nationalId) completed.add(1);
              if (sc.address?.city) completed.add(2);
              if (sc.businessName) completed.add(3);
              if (sc.collateral?.length) completed.add(4);
              setCompletedSteps(completed);
              // FIX 1: Fetch existing KYC docs for converted client
              const serverDocs = await fetchExistingKycDocs(existingClientId);
              if (serverDocs.length > 0) {
                setForm(prev => ({ ...prev, kycDocuments: serverDocs }));
              }
            } catch { setForm(prev => ({ ...prev, paymentPhone: l.phone })); }
          } else if (l.status === 'onboarding' && l.convertedToClient) {
            try {
              const existingClientId = extractId(l.convertedToClient);
              const clientRes = await api.get(`/clients/${existingClientId}`);
              const sc = clientRes.data.data;
              setClientId(existingClientId);
              setForm(prev => ({
                ...prev,
                paymentPhone: sc.phone || l.phone,
                nationalId: sc.nationalId || '',
                dateOfBirth: sc.dateOfBirth ? new Date(sc.dateOfBirth).toISOString().split('T')[0] : '',
                gender: sc.gender || '',
                occupation: sc.occupation || '',
                employer: sc.employer || '',
                monthlyIncome: sc.monthlyIncome || '',
                maritalStatus: sc.maritalStatus || '',
                references: prev.references,
                homeAddress: { street: sc.address?.street || '', city: sc.address?.city || '', county: sc.address?.state || '', postalCode: sc.address?.postalCode || '' },
                homeGps: sc.gpsLocation || null,
                homePhotos: sc.homePhotos || [],
                homeDescription: sc.homeDescription || sc.address?.description || '',
                businessName: sc.businessName || '',
                businessType: sc.businessType || '',
                businessSector: sc.businessSector || '',
                businessAddress: sc.businessAddress || '',
                businessGps: sc.businessGpsLocation || null,
                businessPhotos: sc.businessPhotos || [],
                businessDescription: sc.businessDescription || '',
                collateral: sc.collateral?.length ? sc.collateral : [{ type: '', description: '', estimatedValue: '', image: null }],
                paymentStatus: 'pending',
                paymentMethod: 'stk', paymentCode: '', paymentVerificationMessage: '',
              }));
              const completed = new Set();
              if (sc.nationalId) completed.add(1);
              if (sc.address?.city) completed.add(2);
              if (sc.businessName) completed.add(3);
              if (sc.collateral?.length) completed.add(4);
              setCompletedSteps(completed);
              // FIX 1: Fetch existing KYC docs for onboarding client
              const serverDocs = await fetchExistingKycDocs(existingClientId);
              if (serverDocs.length > 0) {
                setForm(prev => ({ ...prev, kycDocuments: serverDocs }));
              }
            } catch { setForm(prev => ({ ...prev, paymentPhone: l.phone })); }
          } else {
            const localDraft = localStorage.getItem(STORAGE_KEY(leadId));
            if (localDraft) {
              const parsed = JSON.parse(localDraft);
              const localClientId = extractId(parsed.clientId);
              setClientId(localClientId);
              setCurrentStep(parsed.currentStep || 1);
              // FIX 2: Mark step 6 complete if payment was paid
              const completed = new Set(parsed.completedSteps || []);
              if (parsed.form?.paymentStatus === 'paid') completed.add(6);
              setCompletedSteps(completed);
              if (parsed.lastSaved) setLastSaved(new Date(parsed.lastSaved));
              if (localClientId) {
                try {
                  const clientRes = await api.get(`/clients/${localClientId}`);
                  const sc = clientRes.data.data;
                  setForm(prev => ({
                    ...prev, ...parsed.form,
                    homePhotos: sc.homePhotos?.length ? sc.homePhotos : [],
                    businessPhotos: sc.businessPhotos?.length ? sc.businessPhotos : [],
                    collateral: sc.collateral?.length ? sc.collateral : [{ type: '', description: '', estimatedValue: '', image: null }],
                  }));
                  // FIX 1: Fetch existing KYC docs from server for local draft
                  const serverDocs = await fetchExistingKycDocs(localClientId);
                  if (serverDocs.length > 0) {
                    setForm(prev => {
                      const merged = [...(prev.kycDocuments || [])];
                      for (const sDoc of serverDocs) {
                        const existingIdx = merged.findIndex(d => d.documentType === sDoc.documentType);
                        if (existingIdx >= 0) {
                          if (!merged[existingIdx].preview && sDoc.preview) {
                            merged[existingIdx] = { ...merged[existingIdx], preview: sDoc.preview, uploaded: true, serverId: sDoc.serverId };
                          } else {
                            merged[existingIdx] = { ...merged[existingIdx], uploaded: true, serverId: sDoc.serverId };
                          }
                        } else {
                          merged.push(sDoc);
                        }
                      }
                      return { ...prev, kycDocuments: merged };
                    });
                  }
                } catch { setForm(prev => ({ ...prev, ...parsed.form })); }
              } else {
                setForm(prev => ({ ...prev, paymentPhone: l.phone, ...parsed.form }));
              }
              saveDraftToServer(parsed.currentStep, completed, parsed.form, localClientId);
            } else {
              setForm(prev => ({ ...prev, paymentPhone: l.phone }));
            }
          }
        }
        setDraftLoaded(true);
      } catch (e) { navigate('/leads'); }
    };
    loadData();
  }, [leadId]);

  const loadInteractions = useCallback(async () => {
    setInteractionsLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('leadId', leadId);
      if (clientId) params.append('clientId', clientId);
      const res = await api.get(`/clients/interactions?${params.toString()}`);
      setInteractions(res.data.data);
    } catch {}
    setInteractionsLoading(false);
  }, [leadId, clientId]);

  useEffect(() => { if (draftLoaded) loadInteractions(); }, [loadInteractions, draftLoaded]);

  // ─── DRAFT SAVING ───

  const saveDraftToServer = async (step, completed, formData, cId) => {
    const fd = formData !== undefined ? formData : form;
    const st = step || currentStep;
    const cs = completed || completedSteps;
    const cid = cId || clientId;
    try {
      const lightForm = {
        ...fd, homePhotos: [], businessPhotos: [],
        collateral: (fd.collateral || []).map(c => ({ ...c, image: c.image ? 'SAVED_ON_SERVER' : null })),
        // FIX 1: Preserve preview, uploaded flag, and serverId for KYC docs
        kycDocuments: (fd.kycDocuments || []).map(d => ({
          documentType: d.documentType,
          file: null,
          preview: d.preview || null,
          description: d.description || '',
          uploaded: d.uploaded || false,
          serverId: d.serverId || null,
        })),
        contracts: (fd.contracts || []).map(c => ({ ...c, file: null, preview: null })),
      };
      await api.post(`/clients/draft/${leadId}`, {
        clientId: extractId(cid) || null, currentStep: st,
        completedSteps: cs ? [...cs] : [...completedSteps], formData: lightForm,
      });
    } catch {}
  };

  const saveDraftToLocal = (step, completed, formData, cId) => {
    const fd = formData !== undefined ? formData : form;
    const st = step || currentStep;
    const cs = completed || completedSteps;
    const cid = cId || clientId;
    const lightForm = {
      ...fd, homePhotos: [], businessPhotos: [],
      collateral: (fd.collateral || []).map(c => ({ ...c, image: null })),
      // FIX 1: Preserve preview, uploaded flag, and serverId for KYC docs in local draft
      kycDocuments: (fd.kycDocuments || []).map(d => ({
        documentType: d.documentType,
        file: null,
        preview: d.preview || null,
        description: d.description || '',
        uploaded: d.uploaded || false,
        serverId: d.serverId || null,
      })),
      contracts: (fd.contracts || []).map(c => ({ ...c, file: null, preview: null })),
    };
    try {
      localStorage.setItem(STORAGE_KEY(leadId), JSON.stringify({
        form: lightForm, clientId: extractId(cid), currentStep: st,
        completedSteps: [...cs], lastSaved: new Date().toISOString(),
      }));
    } catch {}
  };

  const saveAllDrafts = async (step, completed, formData, cId) => {
    saveDraftToLocal(step, completed, formData, cId);
    await saveDraftToServer(step, completed, formData, cId);
  };

  useEffect(() => {
    if (!draftLoaded) return;
    if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    autoSaveTimerRef.current = setTimeout(() => { saveAllDrafts(); }, 2000);
    return () => { if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current); };
  }, [form, currentStep, completedSteps, clientId, draftLoaded]);

  useEffect(() => {
    const handler = () => { saveDraftToLocal(); };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [form, currentStep, completedSteps, clientId]);

  // ─── HELPERS ───
  const fileToBase64 = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

  const captureGps = (field) => {
    if (!navigator.geolocation) return alert('Geolocation not supported.');
    navigator.geolocation.getCurrentPosition(
      (pos) => setForm(prev => ({ ...prev, [field]: { latitude: pos.coords.latitude, longitude: pos.coords.longitude } })),
      () => alert('Failed to get GPS.'),
      { enableHighAccuracy: true }
    );
  };

  const updateField = (field, value) => setForm(prev => ({ ...prev, [field]: value }));
  const updateNested = (parent, field, value) => setForm(prev => ({ ...prev, [parent]: { ...prev[parent], [field]: value } }));

  // ─── INTERACTIONS ───
  const handleSaveInteraction = async ({ type, content, step, stepTitle }) => {
    setSavingInteraction(true);
    try {
      const res = await api.post('/clients/interactions', {
        lead: leadId, client: extractId(clientId) || null, step, stepTitle, type, content
      });
      setInteractions(prev => [res.data.data, ...prev]);
    } catch (err) { alert(err.response?.data?.message || 'Failed to save'); }
    setSavingInteraction(false);
  };

  // ─── REFERENCE HANDLERS ───
  const checkRefDuplicate = useCallback((index, field, value) => {
    if (refTimers.current[index]) clearTimeout(refTimers.current[index]);
    if (!value || value.length < 4) { setRefChecks(prev => ({ ...prev, [index]: { ...prev[index], [field]: null } })); return; }
    setRefChecks(prev => ({ ...prev, [index]: { ...prev[index], [field]: { loading: true, data: null, error: null } } }));
    refTimers.current[index] = setTimeout(async () => {
      try {
        const res = await api.get(`/clients/check-duplicate?field=${field}&value=${encodeURIComponent(value)}`);
        setRefChecks(prev => ({ ...prev, [index]: { ...prev[index], [field]: { loading: false, data: res.data.data, error: null } } }));
      } catch {
        setRefChecks(prev => ({ ...prev, [index]: { ...prev[index], [field]: { loading: false, data: null, error: 'Failed' } } }));
      }
    }, 600);
  }, []);

  const handleRefChange = (index, field, value) => {
    const newRefs = [...form.references];
    newRefs[index][field] = value;
    updateField('references', newRefs);
    if (field === 'phone') checkRefDuplicate(index, 'phone', value);
    if (field === 'idNumber') checkRefDuplicate(index, 'nationalId', value);
  };
  const addReference = () => { if (form.references.length < 5) updateField('references', [...form.references, { name: '', phone: '', relationship: '', idNumber: '' }]); };
  const removeReference = (i) => { if (form.references.length > 1) updateField('references', form.references.filter((_, idx) => idx !== i)); };

  // ─── COLLATERAL ───
  const handleCollateralChange = (index, field, value) => { const c = [...form.collateral]; c[index][field] = value; updateField('collateral', c); };
  const addCollateral = () => { if (form.collateral.length < 5) updateField('collateral', [...form.collateral, { type: '', description: '', estimatedValue: '', image: null }]); };
  const removeCollateral = (i) => { if (form.collateral.length > 1) updateField('collateral', form.collateral.filter((_, idx) => idx !== i)); };
  const handleCollateralImage = async (index, e) => { const file = e.target.files[0]; if (!file) return; const b64 = await fileToBase64(file); const c = [...form.collateral]; c[index].image = b64; updateField('collateral', c); };

  // ─── PHOTOS ───
  const triggerFileInput = (refKey) => { fileInputRefs.current[refKey]?.click(); };
  const handlePhotosChange = async (e, field) => {
    for (const file of Array.from(e.target.files)) {
      if (file.size > 5 * 1024 * 1024) { alert(`${file.name} exceeds 5MB.`); continue; }
      const b64 = await fileToBase64(file);
      setForm(prev => ({ ...prev, [field]: [...prev[field], { image: b64, description: '' }] }));
    }
    e.target.value = '';
  };
  const updatePhotoDesc = (field, index, value) => { const u = [...form[field]]; u[index].description = value; updateField(field, u); };
  const removePhoto = (field, index) => { updateField(field, form[field].filter((_, i) => i !== index)); };

  // ─── KYC ───
  const handleKycUpload = async (e, type) => {
    const file = e.target.files[0]; if (!file) return;
    const preview = URL.createObjectURL(file);
    const existing = form.kycDocuments.filter(d => d.documentType !== type);
    // FIX 1: Track uploaded state
    setForm(prev => ({ ...prev, kycDocuments: [...existing, { file, documentType: type, description: '', preview, uploaded: false }] }));
  };
  const updateKycDesc = (type, value) => { updateField('kycDocuments', form.kycDocuments.map(d => d.documentType === type ? { ...d, description: value } : d)); };
  const removeKycDoc = (type) => { updateField('kycDocuments', form.kycDocuments.filter(d => d.documentType !== type)); };

  // ─── CONTRACTS ───
  const handleContractUpload = async (e) => {
    for (const file of Array.from(e.target.files)) {
      setForm(prev => ({ ...prev, contracts: [...prev.contracts, { file, description: '', preview: URL.createObjectURL(file) }] }));
    }
    e.target.value = '';
  };
  const updateContractDesc = (index, value) => { const u = [...form.contracts]; u[index].description = value; updateField('contracts', u); };
  const removeContract = (index) => { const u = [...form.contracts]; if (u[index].preview) URL.revokeObjectURL(u[index].preview); updateField('contracts', u.filter((_, i) => i !== index)); };

  // ═══════════════════════════════════════════════════════════════
  // STEP-BY-STEP SERVER SAVE FUNCTIONS
  // ═══════════════════════════════════════════════════════════════

  const saveStep1 = async () => {
    setLoading(true); setError('');
    try {
      const res = await api.post(`/clients/convert-lead/${leadId}`, {
        nationalId: form.nationalId, dateOfBirth: form.dateOfBirth, gender: form.gender,
        occupation: form.occupation, employer: form.employer,
        monthlyIncome: form.monthlyIncome ? parseFloat(form.monthlyIncome) : null,
        maritalStatus: form.maritalStatus,
        references: form.references.filter(r => r.name || r.phone),
        nextOfKin: form.references.length > 0 ? {
          name: form.references[0].name || '', phone: form.references[0].phone || '',
          relationship: form.references[0].relationship || '', nationalId: form.references[0].idNumber || '',
        } : undefined,
        notes: `Refs: ${form.references.map(r => `${r.name} (${r.relationship})`).join(' | ')}`,
        loanOfficerId: form.loanOfficerId || undefined,
        collectionOfficerId: form.collectionOfficerId || undefined,
      });
      const newId = extractId(res.data.data._id);
      setClientId(newId);
      return newId;
    } catch (err) { setError(err.response?.data?.message || 'Failed to create client'); return false; }
    finally { setLoading(false); }
  };

  const updatePersonalInfo = async (cId) => {
    const id = extractId(cId);
    if (!id) { setError('Client ID is missing.'); return false; }
    setLoading(true); setError('');
    try {
      await api.put(`/clients/${id}`, {
        dateOfBirth: form.dateOfBirth, gender: form.gender, occupation: form.occupation,
        employer: form.employer,
        monthlyIncome: form.monthlyIncome ? parseFloat(form.monthlyIncome) : null,
        maritalStatus: form.maritalStatus,
        references: form.references.filter(r => r.name || r.phone),
        notes: `Refs: ${form.references.map(r => `${r.name} (${r.relationship})`).join(' | ')}`,
        loanOfficerId: form.loanOfficerId || undefined,
        collectionOfficerId: form.collectionOfficerId || undefined,
      });
      return true;
    } catch (err) { setError(err.response?.data?.message || 'Failed to update'); return false; }
    finally { setLoading(false); }
  };

  const saveStep2 = async (cId) => {
    const id = extractId(cId) || extractId(clientId);
    if (!id) { setError('Complete Step 1 first.'); return false; }
    setLoading(true); setError('');
    try {
      await api.put(`/clients/${id}`, {
        address: { ...form.homeAddress, country: 'Kenya', type: 'home', description: form.homeDescription, county: form.homeAddress.county, state: form.homeAddress.county },
        gpsLocation: form.homeGps, homePhotos: form.homePhotos, homeDescription: form.homeDescription,
      });
      return true;
    } catch (err) { setError(err.response?.data?.message || 'Failed to save home details'); return false; }
    finally { setLoading(false); }
  };

  const saveStep3 = async (cId) => {
    const id = extractId(cId) || extractId(clientId);
    if (!id) { setError('Complete Step 1 first.'); return false; }
    setLoading(true); setError('');
    try {
      await api.put(`/clients/${id}`, {
        businessName: form.businessName, businessType: form.businessType,
        businessSector: form.businessSector, businessAddress: form.businessAddress,
        businessGpsLocation: form.businessGps, businessPhotos: form.businessPhotos,
        businessDescription: form.businessDescription,
      });
      return true;
    } catch (err) { setError(err.response?.data?.message || 'Failed to save business details'); return false; }
    finally { setLoading(false); }
  };

  const saveStep4 = async (cId) => {
    const id = extractId(cId) || extractId(clientId);
    if (!id) { setError('Complete Step 1 first.'); return false; }
    setLoading(true); setError('');
    try {
      const validCollateral = form.collateral
        .filter(c => c.type && c.type.trim() !== '')
        .map(c => ({ ...c, estimatedValue: parseFloat(c.estimatedValue) || 0 }));
      await api.put(`/clients/${id}`, { collateral: validCollateral });
      return true;
    } catch (err) { setError(err.response?.data?.message || 'Failed to save collateral'); return false; }
    finally { setLoading(false); }
  };

  // FIX 1: Updated uploadKycDocs — skip already-uploaded docs
  const uploadKycDocs = async () => {
    const cid = extractId(clientId);
    if (!cid) { setError('Complete Step 1 first.'); return false; }
    // Check required documents are present (either uploaded to server or selected in form)
    const requiredTypes = REQUIRED_KYC_DOCS.filter(d => d.required).map(d => d.key);
    for (const type of requiredTypes) {
      const doc = form.kycDocuments.find(d => d.documentType === type);
      if (!doc) {
        const label = REQUIRED_KYC_DOCS.find(d => d.key === type)?.label || type;
        setError(`Please upload ${label}.`);
        return false;
      }
      if (!doc.file && !doc.uploaded) {
        const label = REQUIRED_KYC_DOCS.find(d => d.key === type)?.label || type;
        setError(`Please re-upload ${label}.`);
        return false;
      }
    }
    // Only upload documents that have a file (not already on server)
    const docsToUpload = form.kycDocuments.filter(d => d.file);
    if (docsToUpload.length === 0) return true;
    setLoading(true); setError('');
    try {
      for (const doc of docsToUpload) {
        const fd = new FormData();
        fd.append('documentType', doc.documentType); fd.append('document', doc.file); fd.append('notes', doc.description);
        await api.post(`/clients/${cid}/documents`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      }
      // Mark all as uploaded after successful upload
      setForm(prev => ({
        ...prev,
        kycDocuments: prev.kycDocuments.map(d => ({ ...d, uploaded: true, file: null })),
      }));
      return true;
    } catch (err) { setError(err.response?.data?.message || 'Upload failed'); return false; }
    finally { setLoading(false); }
  };

  const normalizePhone = (value) => {
    const clean = String(value || '').replace(/\s/g, '');
    if (/^0\d{9}$/.test(clean)) return `254${clean.slice(1)}`;
    if (/^\+254\d{9}$/.test(clean)) return clean.slice(1);
    if (/^254\d{9}$/.test(clean)) return clean;
    return null;
  };

  const markPaymentPaid = useCallback((receiptNumber, method = form.paymentMethod) => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
    updateField('paymentStatus', 'paid');
    if (receiptNumber) updateField('paymentReceipt', receiptNumber);
    updateField('paymentMethod', method);
    setStepMessage('✅ Payment verified and received!');
    setCompletedSteps(prev => new Set([...prev, 6]));
    setTimeout(() => { setCurrentStep(7); setStepMessage(''); }, 1500);
  }, [form.paymentMethod]);

  const initiatePayment = async () => {
    const cid = extractId(clientId);
    if (!cid) { setError('Complete Step 1 first.'); return; }
    const phone = normalizePhone(form.paymentPhone);
    if (!phone) { setError('Enter a valid Safaricom phone number, e.g. 0712345678.'); return; }
    if (pollIntervalRef.current) { clearInterval(pollIntervalRef.current); pollIntervalRef.current = null; }
    setLoading(true); setError(''); setStepMessage('');
    updateField('paymentStatus', 'processing');
    updateField('paymentMethod', 'stk');
    try {
      const res = await api.post('/payments/initiate-onboarding-fee', { clientId: cid, phone, amount: 700 });
      if (res.data.success) {
        setStepMessage('📱 Check the clients phone and enter the M-Pesa PIN.');
        pollPaymentStatus();
      } else {
        updateField('paymentStatus', 'pending');
        setError(res.data.message || 'Failed to initiate M-Pesa payment.');
      }
    } catch (err) {
      updateField('paymentStatus', 'pending');
      setError(err.response?.data?.message || 'Failed to initiate M-Pesa payment.');
    } finally { setLoading(false); }
  };

  // ═══════════════════════════════════════════════════════════
  // OPTION 2: MANUAL M-PESA TRANSACTION CODE
  // The backend MUST verify the transaction against Safaricom
  // and match the client/amount before returning verified=true.
  // Never trust a receipt code using frontend validation alone.
  // ═══════════════════════════════════════════════════════════
  const verifyMpesaCode = async () => {
    const cid = extractId(clientId);
    if (!cid) { setError('Complete Step 1 first.'); return; }

    const code = String(form.paymentCode || '').trim().toUpperCase();
    if (!/^[A-Z0-9]{8,20}$/.test(code)) {
      setError('Enter a valid M-Pesa transaction code.');
      return;
    }

    const phone = normalizePhone(form.paymentPhone);
    if (!phone) {
      setError('Enter the M-Pesa phone number used for the payment.');
      return;
    }

    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }

    setLoading(true);
    setError('');
    setStepMessage('🔎 Verifying the M-Pesa transaction...');
    updateField('paymentStatus', 'verifying');
    updateField('paymentVerificationMessage', 'Checking transaction with the payment service.');

    try {
      const res = await api.post('/payments/verify-onboarding-fee', {
        clientId: cid,
        mpesaCode: code,
        phone,
        amount: 700,
      });

      const data = res.data?.data || res.data || {};
      const verified = data.verified === true || data.status === 'paid';

      if (!res.data?.success && !verified) {
        throw new Error(res.data?.message || 'The transaction could not be verified.');
      }

      if (!verified) {
        updateField('paymentStatus', 'pending');
        updateField('paymentVerificationMessage', data.message || 'Payment has not reached the system yet.');
        setStepMessage('⏳ Payment not received yet. We will keep checking for a short while...');

        // C2B confirmation can arrive a few seconds after the client's M-Pesa
        // SMS. Retry the exact same server-side verification for up to 60s.
        let attempts = 0;
        const retryTimer = setInterval(async () => {
          attempts += 1;
          try {
            const retryRes = await api.post('/payments/verify-onboarding-fee', {
              clientId: cid,
              mpesaCode: code,
              phone,
              amount: 700,
            });
            const retryData = retryRes.data?.data || retryRes.data || {};
            const retryVerified = retryData.verified === true || retryData.status === 'paid';

            if (retryVerified) {
              clearInterval(retryTimer);
              if (pollIntervalRef.current === retryTimer) pollIntervalRef.current = null;
              updateField('paymentCode', code);
              updateField('paymentVerificationMessage', retryData.message || 'Transaction verified successfully.');
              markPaymentPaid(retryData.receiptNumber || retryData.mpesaCode || code, 'mpesa_code');
              setLoading(false);
            } else if (attempts >= 12) {
              clearInterval(retryTimer);
              if (pollIntervalRef.current === retryTimer) pollIntervalRef.current = null;
              updateField('paymentStatus', 'pending');
              setStepMessage('');
              setError(retryData.message || 'Transaction has not reached the system yet. Please wait a little longer and verify again.');
              setLoading(false);
            }
          } catch (retryErr) {
            if (attempts >= 12) {
              clearInterval(retryTimer);
              if (pollIntervalRef.current === retryTimer) pollIntervalRef.current = null;
              updateField('paymentStatus', 'pending');
              setStepMessage('');
              setError(retryErr.response?.data?.message || retryErr.message || 'Unable to verify the M-Pesa transaction.');
              setLoading(false);
            }
          }
        }, 5000);
        pollIntervalRef.current = retryTimer;
        return;
      }

      const receiptNumber = data.receiptNumber || data.mpesaCode || code;
      updateField('paymentCode', code);
      updateField('paymentVerificationMessage', data.message || 'Transaction verified successfully.');
      markPaymentPaid(receiptNumber, 'mpesa_code');
    } catch (err) {
      updateField('paymentStatus', 'pending');
      updateField('paymentVerificationMessage', '');
      setStepMessage('');
      setError(err.response?.data?.message || err.message || 'Unable to verify the M-Pesa transaction.');
    } finally {
      setLoading(false);
    }
  };

  // ═══════════════════════════════════════════════════════════
  // CANCEL STK PUSH — stops polling and resets state
  // ═══════════════════════════════════════════════════════════
  const cancelPaymentPolling = () => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
    updateField('paymentStatus', 'pending');
    setStepMessage('');
    setError('');
  };

  const pollPaymentStatus = () => {
    const cid = extractId(clientId);
    let n = 0;
    const iv = setInterval(async () => {
      n++;
      try {
        const res = await api.get(`/payments/onboarding-fee-status/${cid}`);
        const data = res.data?.data || {};
        const { status, receiptNumber } = data;
        if (status === 'paid') {
          clearInterval(iv); pollIntervalRef.current = null;
          markPaymentPaid(receiptNumber, 'stk');
        } else if (status === 'failed') {
          clearInterval(iv); pollIntervalRef.current = null;
          updateField('paymentStatus', 'pending'); setError('Payment failed.');
        }
      } catch {}
      if (n >= 40) {
        clearInterval(iv);
        pollIntervalRef.current = null;
        updateField('paymentStatus', 'pending');
        setError('Timeout waiting for payment confirmation. You can use Option 2 and enter the M-Pesa transaction code if the payment was completed.');
      }
    }, 5000);
    pollIntervalRef.current = iv;
  };

  const uploadContracts = async () => {
    const cid = extractId(clientId);
    if (!cid) { setError('Complete Step 1 first.'); return false; }
    if (form.contracts.some(d => !d.file)) { setError('Re-select contracts.'); return false; }
    setLoading(true); setError('');
    try {
      for (const doc of form.contracts) {
        const fd = new FormData();
        fd.append('documentType', 'contract');
        fd.append('document', doc.file);
        fd.append('notes', doc.description);
        await api.post(`/clients/${cid}/documents`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      }
      return true;
    } catch (err) { setError(err.response?.data?.message || 'Upload failed'); return false; }
    finally { setLoading(false); }
  };

  // ═══════════════════════════════════════════════════════════════
  // FIX 2: handleNext — step 6 checks paymentStatus === 'paid'
  // ═══════════════════════════════════════════════════════════════
  const handleNext = async () => {
    setError(''); setStepMessage('');

    switch (currentStep) {
      case 1: {
        if (!form.nationalId) { setError('National ID is required.'); return; }
        if (isNationalIdBlocked) { setError('Duplicate National ID detected. Cannot proceed.'); return; }
        let cId = clientId;
        if (!cId) {
          cId = await saveStep1();
          if (!cId) return;
        } else {
          const ok = await updatePersonalInfo(cId);
          if (!ok) return;
        }
        setCompletedSteps(p => new Set([...p, 1]));
        setCurrentStep(2);
        break;
      }
      case 2: {
        const ok = await saveStep2();
        if (!ok) return;
        setCompletedSteps(p => new Set([...p, 2]));
        setCurrentStep(3);
        break;
      }
      case 3: {
        const ok = await saveStep3();
        if (!ok) return;
        setCompletedSteps(p => new Set([...p, 3]));
        setCurrentStep(4);
        break;
      }
      case 4: {
        const ok = await saveStep4();
        if (!ok) return;
        setCompletedSteps(p => new Set([...p, 4]));
        setCurrentStep(5);
        break;
      }
      case 5: {
        const ok = await uploadKycDocs();
        if (!ok) return;
        setCompletedSteps(p => new Set([...p, 5]));
        setCurrentStep(6);
        break;
      }
      case 6: {
        // FIX 2: Allow proceeding only when payment status is 'paid'
        if (form.paymentStatus !== 'paid') {
          setError('Please complete the payment first before proceeding.');
          return;
        }
        setCompletedSteps(p => new Set([...p, 6]));
        setCurrentStep(7);
        break;
      }
      case 7: {
        const ok = await uploadContracts();
        if (!ok) return;
        setCompletedSteps(p => new Set([...p, 7]));
        setCurrentStep(8);
        break;
      }
      case 8: {
        await handleSubmit();
        break;
      }
      default: break;
    }
  };

  const handleBack = () => {
    setError(''); setStepMessage('');
    if (currentStep > 1) setCurrentStep(prev => prev - 1);
  };

  const handleSubmit = async () => {
    setLoading(true); setError('');
    try {
      const id = extractId(clientId);
      await api.put(`/clients/${id}/submit`);
      await api.put(`/leads/${leadId}`, { status: 'onboarded' });
      try { await api.delete(`/clients/draft/${leadId}`); } catch {}
      localStorage.removeItem(STORAGE_KEY(leadId));
      navigate('/clients');
    } catch (err) {
      setError(err.response?.data?.message || 'Submission failed');
    } finally {
      setLoading(false);
    }
  };

  const navigateToClient = (cId) => { navigate(`/clients/${cId}`); };

  // ═══════════════════════════════════════════════════════════════
  // STEP RENDERERS
  // ═══════════════════════════════════════════════════════════════

  const renderStep1 = () => (
    <div style={{ background: T.card, borderRadius: T.radiusXl, padding: '28px 32px', boxShadow: T.shadowMd, border: `1px solid ${T.border}`, minHeight: 400 }}>
      <div style={{ marginBottom: 24 }}>
        <h4 style={{ margin: '0 0 4px', fontSize: 18, fontWeight: 700, color: T.text }}>👤 Personal Information</h4>
        <p style={{ margin: 0, fontSize: 13, color: T.textMuted }}>Enter the client's personal details and identity information.</p>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        <div>
          <label style={labelStyle}>National ID *</label>
          <input style={inputStyle} value={form.nationalId} onChange={e => { updateField('nationalId', e.target.value); check('nationalId', e.target.value, extractId(clientId)); }} placeholder="e.g. 12345678" />
          <DuplicateBadge result={checks.nationalId} blocking navigateToClient={navigateToClient} />
        </div>
        <div>
          <label style={labelStyle}>Date of Birth *</label>
          <input type="date" style={inputStyle} value={form.dateOfBirth} onChange={e => updateField('dateOfBirth', e.target.value)} />
        </div>
        <div>
          <label style={labelStyle}>Gender *</label>
          <select style={selectStyle} value={form.gender} onChange={e => updateField('gender', e.target.value)}>
            <option value="">Select...</option>
            <option value="male">Male</option>
            <option value="female">Female</option>
            <option value="other">Other</option>
          </select>
        </div>
        <div>
          <label style={labelStyle}>Marital Status</label>
          <select style={selectStyle} value={form.maritalStatus} onChange={e => updateField('maritalStatus', e.target.value)}>
            <option value="">Select...</option>
            <option value="single">Single</option>
            <option value="married">Married</option>
            <option value="divorced">Divorced</option>
            <option value="widowed">Widowed</option>
          </select>
        </div>
        <div>
          <label style={labelStyle}>Occupation *</label>
          <input style={inputStyle} value={form.occupation} onChange={e => updateField('occupation', e.target.value)} placeholder="e.g. Teacher" />
        </div>
        <div>
          <label style={labelStyle}>Employer</label>
          <input style={inputStyle} value={form.employer} onChange={e => updateField('employer', e.target.value)} placeholder="e.g. Ministry of Education" />
        </div>
        <div>
          <label style={labelStyle}>Monthly Income (KES)</label>
          <input type="number" style={inputStyle} value={form.monthlyIncome} onChange={e => updateField('monthlyIncome', e.target.value)} placeholder="e.g. 50000" />
        </div>
      </div>

      <div style={{ marginTop: 24, padding: 16, borderRadius: T.radius, background: T.primaryLight, border: '1px solid #c7d2fe' }}>
        <h5 style={{ margin: '0 0 5px', fontSize: 14, color: T.primaryDark }}>👥 Responsible Officers</h5>
        <p style={{ margin: '0 0 12px', fontSize: 12, color: T.textSecondary }}>The system automatically assigns active officers in the client branch where available. You can override that assignment here during onboarding.</p>
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
          <div><label style={labelStyle}>Loan Officer</label><select style={selectStyle} value={form.loanOfficerId} onChange={e => updateField('loanOfficerId', e.target.value)}><option value="">Automatic assignment</option>{availableOfficers.filter(o => String(o.role?.name || o.roleName || o.role || '').toLowerCase() === 'loan officer').map(o => <option key={o._id} value={o._id}>{o.firstName} {o.lastName}{o.branch?.name ? ` — ${o.branch.name}` : ''}</option>)}</select></div>
          <div><label style={labelStyle}>Collection Officer</label><select style={selectStyle} value={form.collectionOfficerId} onChange={e => updateField('collectionOfficerId', e.target.value)}><option value="">Automatic assignment</option>{availableOfficers.filter(o => String(o.role?.name || o.roleName || o.role || '').toLowerCase() === 'collection officer').map(o => <option key={o._id} value={o._id}>{o.firstName} {o.lastName}{o.branch?.name ? ` — ${o.branch.name}` : ''}</option>)}</select></div>
        </div>
      </div>

      <div style={{ marginTop: 28 }}>
        <SectionHeader title="References" onAdd={addReference} addLabel="Add Reference" maxItems={5} currentCount={form.references.length} />
        {form.references.map((ref, i) => (
          <div key={i} style={{
            padding: 16, borderRadius: T.radius, marginBottom: 12,
            background: T.inputBg, border: `1px solid ${T.border}`,
            display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr auto', gap: 12, alignItems: 'end',
          }}>
            <div>
              <label style={{ ...labelStyle, fontSize: 12 }}>Name</label>
              <input style={{ ...inputStyle, fontSize: 13, padding: '8px 12px' }} value={ref.name} onChange={e => handleRefChange(i, 'name', e.target.value)} placeholder="Full name" />
            </div>
            <div>
              <label style={{ ...labelStyle, fontSize: 12 }}>Phone</label>
              <input style={{ ...inputStyle, fontSize: 13, padding: '8px 12px' }} value={ref.phone} onChange={e => handleRefChange(i, 'phone', e.target.value)} placeholder="0712345678" />
              <DuplicateBadge result={refChecks[i]?.phone} />
            </div>
            <div>
              <label style={{ ...labelStyle, fontSize: 12 }}>Relationship</label>
              <input style={{ ...inputStyle, fontSize: 13, padding: '8px 12px' }} value={ref.relationship} onChange={e => handleRefChange(i, 'relationship', e.target.value)} placeholder="e.g. Spouse" />
            </div>
            <div>
              <label style={{ ...labelStyle, fontSize: 12 }}>ID Number</label>
              <input style={{ ...inputStyle, fontSize: 13, padding: '8px 12px' }} value={ref.idNumber} onChange={e => handleRefChange(i, 'idNumber', e.target.value)} placeholder="National ID" />
              <DuplicateBadge result={refChecks[i]?.idNumber} />
            </div>
            <div style={{ paddingBottom: 2 }}>
              <RemoveButton onClick={() => removeReference(i)} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div style={{ background: T.card, borderRadius: T.radiusXl, padding: '28px 32px', boxShadow: T.shadowMd, border: `1px solid ${T.border}`, minHeight: 400 }}>
      <div style={{ marginBottom: 24 }}>
        <h4 style={{ margin: '0 0 4px', fontSize: 18, fontWeight: 700, color: T.text }}>🏠 Home Address</h4>
        <p style={{ margin: 0, fontSize: 13, color: T.textMuted }}>Capture the client's residential address and location details.</p>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        <div style={{ gridColumn: '1 / -1' }}>
          <label style={labelStyle}>Street Address</label>
          <input style={inputStyle} value={form.homeAddress.street} onChange={e => updateNested('homeAddress', 'street', e.target.value)} placeholder="e.g. 123 Kenyatta Ave" />
        </div>
        <div>
          <label style={labelStyle}>City *</label>
          <input style={inputStyle} value={form.homeAddress.city} onChange={e => updateNested('homeAddress', 'city', e.target.value)} placeholder="e.g. Nairobi" />
        </div>
        <div>
          <label style={labelStyle}>County *</label>
          <input style={inputStyle} value={form.homeAddress.county} onChange={e => updateNested('homeAddress', 'county', e.target.value)} placeholder="e.g. Nairobi" />
        </div>
        <div>
          <label style={labelStyle}>Postal Code</label>
          <input style={inputStyle} value={form.homeAddress.postalCode} onChange={e => updateNested('homeAddress', 'postalCode', e.target.value)} placeholder="e.g. 00100" />
        </div>
      </div>
      <div style={{ marginTop: 16 }}>
        <label style={labelStyle}>Description</label>
        <textarea rows={3} style={{ ...inputStyle, resize: 'vertical' }} value={form.homeDescription} onChange={e => updateField('homeDescription', e.target.value)} placeholder="Landmarks, nearest school, estate name..." />
      </div>
      <div style={{ marginTop: 20, display: 'flex', gap: 12, alignItems: 'center' }}>
        <button type="button" onClick={() => captureGps('homeGps')} style={{
          padding: '9px 20px', borderRadius: 10, border: 'none',
          background: `linear-gradient(135deg, ${T.primary} 0%, ${T.primaryDark} 100%)`,
          color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer',
          boxShadow: `0 2px 8px ${T.primary}30`, display: 'flex', alignItems: 'center', gap: 6,
        }}>📍 Capture GPS</button>
        {form.homeGps && (
          <span style={{ fontSize: 12, color: T.success, fontWeight: 500, padding: '6px 14px', background: T.successLight, borderRadius: 8, border: `1px solid ${T.successBorder}` }}>
            ✅ {form.homeGps.latitude.toFixed(5)}, {form.homeGps.longitude.toFixed(5)}
          </span>
        )}
      </div>
      <div style={{ marginTop: 24 }}>
        <SectionHeader title="Home Photos" onAdd={() => triggerFileInput('homePhotos')} addLabel="Add Photo" />
        <input type="file" accept="image/*" multiple ref={el => fileInputRefs.current.homePhotos = el} style={{ display: 'none' }} onChange={e => handlePhotosChange(e, 'homePhotos')} />
        <PhotoGrid photos={form.homePhotos} field="homePhotos" updatePhotoDesc={updatePhotoDesc} removePhoto={removePhoto} />
      </div>
    </div>
  );

  const renderStep3 = () => (
    <div style={{ background: T.card, borderRadius: T.radiusXl, padding: '28px 32px', boxShadow: T.shadowMd, border: `1px solid ${T.border}`, minHeight: 400 }}>
      <div style={{ marginBottom: 24 }}>
        <h4 style={{ margin: '0 0 4px', fontSize: 18, fontWeight: 700, color: T.text }}>💼 Business Details</h4>
        <p style={{ margin: 0, fontSize: 13, color: T.textMuted }}>Information about the client's business or source of income.</p>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        <div>
          <label style={labelStyle}>Business Name *</label>
          <input style={inputStyle} value={form.businessName} onChange={e => updateField('businessName', e.target.value)} placeholder="e.g. ABC Traders" />
        </div>
        <div>
          <label style={labelStyle}>Business Type</label>
          <select style={selectStyle} value={form.businessType} onChange={e => updateField('businessType', e.target.value)}>
            <option value="">Select...</option>
            <option value="sole_proprietor">Sole Proprietor</option>
            <option value="partnership">Partnership</option>
            <option value="limited_company">Limited Company</option>
            <option value="ngo">NGO</option>
            <option value="other">Other</option>
          </select>
        </div>
        <div>
          <label style={labelStyle}>Business Sector</label>
          <input style={inputStyle} value={form.businessSector} onChange={e => updateField('businessSector', e.target.value)} placeholder="e.g. Retail, Agriculture" />
        </div>
        <div>
          <label style={labelStyle}>Business Address</label>
          <input style={inputStyle} value={form.businessAddress} onChange={e => updateField('businessAddress', e.target.value)} placeholder="e.g. 45 Moi Avenue, Nairobi" />
        </div>
      </div>
      <div style={{ marginTop: 16 }}>
        <label style={labelStyle}>Business Description</label>
        <textarea rows={3} style={{ ...inputStyle, resize: 'vertical' }} value={form.businessDescription} onChange={e => updateField('businessDescription', e.target.value)} placeholder="What does the business do? How long has it been operating?" />
      </div>
      <div style={{ marginTop: 20, display: 'flex', gap: 12, alignItems: 'center' }}>
        <button type="button" onClick={() => captureGps('businessGps')} style={{
          padding: '9px 20px', borderRadius: 10, border: 'none',
          background: `linear-gradient(135deg, ${T.primary} 0%, ${T.primaryDark} 100%)`,
          color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer',
          boxShadow: `0 2px 8px ${T.primary}30`, display: 'flex', alignItems: 'center', gap: 6,
        }}>📍 Capture GPS</button>
        {form.businessGps && (
          <span style={{ fontSize: 12, color: T.success, fontWeight: 500, padding: '6px 14px', background: T.successLight, borderRadius: 8, border: `1px solid ${T.successBorder}` }}>
            ✅ {form.businessGps.latitude.toFixed(5)}, {form.businessGps.longitude.toFixed(5)}
          </span>
        )}
      </div>
      <div style={{ marginTop: 24 }}>
        <SectionHeader title="Business Photos" onAdd={() => triggerFileInput('businessPhotos')} addLabel="Add Photo" />
        <input type="file" accept="image/*" multiple ref={el => fileInputRefs.current.businessPhotos = el} style={{ display: 'none' }} onChange={e => handlePhotosChange(e, 'businessPhotos')} />
        <PhotoGrid photos={form.businessPhotos} field="businessPhotos" updatePhotoDesc={updatePhotoDesc} removePhoto={removePhoto} />
      </div>
    </div>
  );

  const renderStep4 = () => (
    <div style={{ background: T.card, borderRadius: T.radiusXl, padding: '28px 32px', boxShadow: T.shadowMd, border: `1px solid ${T.border}`, minHeight: 400 }}>
      <div style={{ marginBottom: 24 }}>
        <h4 style={{ margin: '0 0 4px', fontSize: 18, fontWeight: 700, color: T.text }}>🔒 Collateral</h4>
        <p style={{ margin: 0, fontSize: 13, color: T.textMuted }}>Add collateral items to secure the loan.</p>
      </div>
      <SectionHeader title="Collateral Items" onAdd={addCollateral} addLabel="Add Item" maxItems={5} currentCount={form.collateral.length} />
      {form.collateral.map((item, i) => (
        <div key={i} style={{
          padding: 20, borderRadius: T.radius, marginBottom: 14,
          background: T.inputBg, border: `1px solid ${T.border}`,
        }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr auto', gap: 14, alignItems: 'end', marginBottom: item.image ? 14 : 0 }}>
            <div>
              <label style={{ ...labelStyle, fontSize: 12 }}>Type *</label>
              <input style={{ ...inputStyle, fontSize: 13, padding: '8px 12px' }} value={item.type} onChange={e => handleCollateralChange(i, 'type', e.target.value)} placeholder="e.g. Title Deed, Logbook" />
            </div>
            <div>
              <label style={{ ...labelStyle, fontSize: 12 }}>Estimated Value (KES)</label>
              <input type="number" style={{ ...inputStyle, fontSize: 13, padding: '8px 12px' }} value={item.estimatedValue} onChange={e => handleCollateralChange(i, 'estimatedValue', e.target.value)} placeholder="e.g. 500000" />
            </div>
            <div>
              <label style={{ ...labelStyle, fontSize: 12 }}>Description</label>
              <input style={{ ...inputStyle, fontSize: 13, padding: '8px 12px' }} value={item.description} onChange={e => handleCollateralChange(i, 'description', e.target.value)} placeholder="Brief description" />
            </div>
            <div style={{ paddingBottom: 2 }}><RemoveButton onClick={() => removeCollateral(i)} /></div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            <button type="button" onClick={() => fileInputRefs.current[`collateral-${i}`]?.click()} style={{
              padding: '7px 16px', borderRadius: 8, border: `1.5px dashed ${T.border}`,
              background: T.card, color: T.textSecondary, fontSize: 12, cursor: 'pointer',
              display: 'flex', alignItems: 'center', gap: 6, transition: 'all 0.15s',
            }}>📷 Upload Image</button>
            <input type="file" accept="image/*" ref={el => fileInputRefs.current[`collateral-${i}`] = el} style={{ display: 'none' }} onChange={e => handleCollateralImage(i, e)} />
            {item.image && <img src={item.image} alt="" style={{ width: 60, height: 60, objectFit: 'cover', borderRadius: 8, border: `1px solid ${T.border}` }} />}
          </div>
        </div>
      ))}
    </div>
  );

  // ═══════════════════════════════════════════════════════════════
  // FIX 1: KYC step — shows already-uploaded docs with server preview
  // ═══════════════════════════════════════════════════════════════
  const renderStep5 = () => (
    <div style={{ background: T.card, borderRadius: T.radiusXl, padding: '28px 32px', boxShadow: T.shadowMd, border: `1px solid ${T.border}`, minHeight: 400 }}>
      <div style={{ marginBottom: 24 }}>
        <h4 style={{ margin: '0 0 4px', fontSize: 18, fontWeight: 700, color: T.text }}>🪪 KYC Documents</h4>
        <p style={{ margin: 0, fontSize: 13, color: T.textMuted }}>Upload required identification documents. Documents marked with * are mandatory.</p>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
        {REQUIRED_KYC_DOCS.map(docType => {
          const existing = form.kycDocuments.find(d => d.documentType === docType.key);
          const hasPreview = existing?.preview && (existing.preview.startsWith('http') || existing.preview.startsWith('data:') || existing.preview.startsWith('blob:') || existing.preview.startsWith('/'));
          const isUploaded = existing?.uploaded;

          return (
            <div key={docType.key} style={{
              borderRadius: T.radius, border: `2px dashed ${existing ? T.successBorder : T.border}`,
              background: existing ? T.successLight : T.card, padding: 20,
              transition: 'all 0.2s ease', position: 'relative',
            }}>
              {isUploaded && (
                <span style={{
                  position: 'absolute', top: 10, right: 10, padding: '3px 10px',
                  borderRadius: 6, fontSize: 10, fontWeight: 700,
                  background: T.success, color: '#fff', letterSpacing: '0.02em',
                }}>UPLOADED</span>
              )}
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
                <span style={{ fontSize: 20 }}>{docType.required ? '📄' : '📎'}</span>
                <div>
                  <p style={{ margin: 0, fontSize: 14, fontWeight: 600, color: T.text }}>
                    {docType.label}
                    {docType.required && <span style={{ color: T.danger, marginLeft: 4 }}>*</span>}
                  </p>
                </div>
              </div>

              {hasPreview ? (
                <div style={{ marginBottom: 12 }}>
                  <img
                    src={existing.preview}
                    alt={docType.label}
                    style={{
                      width: '100%', height: 140, objectFit: 'contain',
                      borderRadius: 10, border: `1px solid ${T.successBorder}`,
                      background: '#fff',
                    }}
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                </div>
              ) : isUploaded ? (
                <div style={{
                  height: 100, borderRadius: 10, border: `1px solid ${T.successBorder}`,
                  background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  marginBottom: 12, color: T.success, fontSize: 13, fontWeight: 500,
                }}>
                  ✅ Document saved on server
                </div>
              ) : null}

              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <button type="button" onClick={() => fileInputRefs.current[`kyc-${docType.key}`]?.click()} style={{
                  padding: '8px 16px', borderRadius: 8, border: `1.5px solid ${existing ? T.success : T.primary}`,
                  background: existing ? T.successLight : T.primaryLight,
                  color: existing ? T.success : T.primary, fontSize: 12, fontWeight: 600,
                  cursor: 'pointer', transition: 'all 0.15s', flex: 1,
                }}>
                  {existing ? 'Replace' : 'Choose File'}
                </button>
                {existing && (
                  <RemoveButton onClick={() => removeKycDoc(docType.key)} tooltip="Remove document" />
                )}
              </div>
              <input
                type="file"
                accept="image/*,.pdf"
                ref={el => fileInputRefs.current[`kyc-${docType.key}`] = el}
                style={{ display: 'none' }}
                onChange={e => handleKycUpload(e, docType.key)}
              />
              {existing && (
                <input
                  style={{ ...inputStyle, fontSize: 12, padding: '8px 12px', marginTop: 10 }}
                  placeholder="Notes (optional)"
                  value={existing.description}
                  onChange={e => updateKycDesc(docType.key, e.target.value)}
                />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );

  // ═══════════════════════════════════════════════════════════════
  // FIX 2: Fee payment step — Next enabled only when paid
  // ═══════════════════════════════════════════════════════════════
  const renderStep6 = () => {
    const isPaid = form.paymentStatus === 'paid';
    const isProcessing = form.paymentStatus === 'processing';
    const isVerifying = form.paymentStatus === 'verifying';
    const useStk = form.paymentMethod === 'stk';
    const useCode = form.paymentMethod === 'mpesa_code';

    return (
      <div style={{ background: T.card, borderRadius: T.radiusXl, padding: '28px 32px', boxShadow: T.shadowMd, border: `1px solid ${T.border}`, minHeight: 400 }}>
        <div style={{ marginBottom: 24 }}>
          <h4 style={{ margin: '0 0 4px', fontSize: 18, fontWeight: 700, color: T.text }}>💳 Registration Fee</h4>
          <p style={{ margin: 0, fontSize: 13, color: T.textMuted }}>Pay the KES 700 onboarding registration fee via M-Pesa.</p>
        </div>

        <div style={{
          maxWidth: 560, margin: '0 auto', padding: 28, borderRadius: T.radiusXl,
          background: isPaid ? T.successLight : isProcessing || isVerifying ? T.warningLight : T.inputBg,
          border: `2px solid ${isPaid ? T.successBorder : isProcessing || isVerifying ? T.warningBorder : T.border}`,
          textAlign: 'center',
        }}>
          <div style={{
            width: 72, height: 72, borderRadius: '50%', margin: '0 auto 16px',
            display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 32,
            background: isPaid ? T.success : isProcessing || isVerifying ? T.warning : T.primaryLight,
            color: isPaid ? '#fff' : isProcessing || isVerifying ? '#fff' : T.primary,
          }}>
            {isPaid ? '✓' : isProcessing || isVerifying ? '⏳' : '💰'}
          </div>

          <p style={{ margin: '0 0 4px', fontSize: 22, fontWeight: 800, color: T.text }}>KES 700</p>
          <p style={{ margin: '0 0 20px', fontSize: 13, color: T.textMuted }}>Onboarding Registration Fee</p>

          {isPaid ? (
            <div style={{ padding: '14px 20px', borderRadius: 12, background: T.success, color: '#fff', marginBottom: 16 }}>
              <p style={{ margin: 0, fontSize: 15, fontWeight: 700 }}>✅ Payment Confirmed</p>
              {form.paymentReceipt && <p style={{ margin: '4px 0 0', fontSize: 12, opacity: 0.9 }}>Receipt: {form.paymentReceipt}</p>}
              {form.paymentMethod === 'mpesa_code' && (
                <p style={{ margin: '4px 0 0', fontSize: 11, opacity: 0.9 }}>Verified using M-Pesa transaction code</p>
              )}
            </div>
          ) : isProcessing ? (
            <div style={{ marginBottom: 16 }}>
              <div style={{ padding: '14px 20px', borderRadius: 12, background: T.warning, color: '#fff', marginBottom: 12 }}>
                <p style={{ margin: 0, fontSize: 14, fontWeight: 700 }}>⏳ Waiting for M-Pesa confirmation...</p>
                <p style={{ margin: '4px 0 0', fontSize: 11, opacity: 0.9 }}>Check the client phone and enter the PIN.</p>
              </div>
              <button type="button" onClick={cancelPaymentPolling} style={{
                padding: '8px 20px', borderRadius: 8, border: `1px solid ${T.warningBorder}`,
                background: T.warningLight, color: '#92400e', fontSize: 12, fontWeight: 600, cursor: 'pointer',
              }}>Cancel STK</button>
            </div>
          ) : isVerifying ? (
            <div style={{ padding: '14px 20px', borderRadius: 12, background: T.warning, color: '#fff', marginBottom: 16 }}>
              <p style={{ margin: 0, fontSize: 14, fontWeight: 700 }}>🔎 Verifying M-Pesa transaction...</p>
              <p style={{ margin: '4px 0 0', fontSize: 11, opacity: 0.9 }}>Please wait while the transaction is checked.</p>
            </div>
          ) : (
            <div style={{ textAlign: 'left' }}>
              <div style={{ display: 'flex', gap: 8, marginBottom: 18 }}>
                <button type="button" onClick={() => { setError(''); setStepMessage(''); updateField('paymentMethod', 'stk'); updateField('paymentStatus', 'pending'); }} style={{
                  flex: 1, padding: '10px 12px', borderRadius: 10,
                  border: `1.5px solid ${useStk ? T.primary : T.border}`,
                  background: useStk ? T.primaryLight : T.card, color: useStk ? T.primary : T.textSecondary,
                  fontSize: 12, fontWeight: 700, cursor: 'pointer',
                }}>1. Prompt on Phone</button>
                <button type="button" onClick={() => { setError(''); setStepMessage(''); updateField('paymentMethod', 'mpesa_code'); updateField('paymentStatus', 'pending'); }} style={{
                  flex: 1, padding: '10px 12px', borderRadius: 10,
                  border: `1.5px solid ${useCode ? T.primary : T.border}`,
                  background: useCode ? T.primaryLight : T.card, color: useCode ? T.primary : T.textSecondary,
                  fontSize: 12, fontWeight: 700, cursor: 'pointer',
                }}>2. Enter M-Pesa Code</button>
              </div>

              {useStk ? (
                <>
                  <div style={{ marginBottom: 14 }}>
                    <label style={labelStyle}>M-Pesa Phone Number *</label>
                    <input style={inputStyle} value={form.paymentPhone} onChange={e => updateField('paymentPhone', e.target.value)} placeholder="e.g. 0712345678" />
                    <p style={{ margin: '6px 0 0', fontSize: 11, color: T.textMuted }}>An M-Pesa prompt will be sent to this number.</p>
                  </div>
                  <button type="button" onClick={initiatePayment} disabled={loading} style={{
                    width: '100%', padding: '13px 24px', borderRadius: 12, border: 'none',
                    background: loading ? '#c7d2fe' : `linear-gradient(135deg, ${T.success} 0%, #047857 100%)`,
                    color: '#fff', fontSize: 15, fontWeight: 700, cursor: loading ? 'not-allowed' : 'pointer',
                  }}>
                    {loading ? 'Processing...' : '📱 Send M-Pesa Prompt'}
                  </button>
                </>
              ) : (
                <>
                  <div style={{ padding: '12px 14px', borderRadius: 10, background: '#eff6ff', border: '1px solid #bfdbfe', marginBottom: 14 }}>
                    <p style={{ margin: '0 0 8px', fontSize: 12, color: '#1e40af', lineHeight: 1.5 }}>
                      <strong>Option 2:</strong> Pay exactly KES 700 via M-Pesa using the Paybill and the client's National ID as the Account Number, then enter the transaction code from the official M-Pesa confirmation SMS.
                    </p>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                      <div style={{ padding: '8px 10px', background: '#fff', borderRadius: 8, border: '1px solid #dbeafe' }}>
                        <div style={{ fontSize: 10, color: '#64748b', textTransform: 'uppercase', fontWeight: 700 }}>Paybill</div>
                        <div style={{ fontSize: 15, color: '#1e3a8a', fontWeight: 800 }}>{manualPaymentInfo.paybillNumber || 'Loading...'}</div>
                      </div>
                      <div style={{ padding: '8px 10px', background: '#fff', borderRadius: 8, border: '1px solid #dbeafe' }}>
                        <div style={{ fontSize: 10, color: '#64748b', textTransform: 'uppercase', fontWeight: 700 }}>Account (National ID)</div>
                        <div style={{ fontSize: 12, color: '#1e3a8a', fontWeight: 800, wordBreak: 'break-all' }}>{manualPaymentInfo.accountReference || form.nationalId || 'Loading...'}</div>
                      </div>
                    </div>
                    <p style={{ margin: '8px 0 0', fontSize: 10.5, color: '#475569', lineHeight: 1.45 }}>
                      <strong>Important:</strong> Use the client's National ID exactly as the Account Number. The system uses Safaricom's C2B confirmation as the source of truth; a typed transaction code alone cannot be accepted. If the client has an active loan, a KES 700 payment is treated as a loan repayment instead of an onboarding fee.
                    </p>
                  </div>
                  <div style={{ marginBottom: 14 }}>
                    <label style={labelStyle}>M-Pesa Phone Number *</label>
                    <input style={inputStyle} value={form.paymentPhone} onChange={e => updateField('paymentPhone', e.target.value)} placeholder="e.g. 0712345678" />
                  </div>
                  <div style={{ marginBottom: 14 }}>
                    <label style={labelStyle}>M-Pesa Transaction Code *</label>
                    <input
                      style={{ ...inputStyle, textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 700 }}
                      value={form.paymentCode}
                      maxLength={20}
                      onChange={e => { updateField('paymentCode', e.target.value.toUpperCase().replace(/\s/g, '')); updateField('paymentStatus', 'pending'); }}
                      placeholder="e.g. ABC123XYZ"
                      autoComplete="off"
                    />
                    <p style={{ margin: '6px 0 0', fontSize: 11, color: T.textMuted }}>Use the transaction code from the official M-Pesa confirmation SMS.</p>
                  </div>
                  <button type="button" onClick={verifyMpesaCode} disabled={loading || !form.paymentCode.trim()} style={{
                    width: '100%', padding: '13px 24px', borderRadius: 12, border: 'none',
                    background: loading || !form.paymentCode.trim() ? '#c7d2fe' : `linear-gradient(135deg, ${T.primary} 0%, ${T.primaryDark} 100%)`,
                    color: '#fff', fontSize: 15, fontWeight: 700, cursor: loading || !form.paymentCode.trim() ? 'not-allowed' : 'pointer',
                  }}>
                    {loading ? 'Verifying...' : '🔎 Verify M-Pesa Code'}
                  </button>
                  {form.paymentVerificationMessage && (
                    <p style={{ margin: '10px 0 0', fontSize: 11, color: T.textSecondary, textAlign: 'center' }}>{form.paymentVerificationMessage}</p>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderStep7 = () => (
    <div style={{ background: T.card, borderRadius: T.radiusXl, padding: '28px 32px', boxShadow: T.shadowMd, border: `1px solid ${T.border}`, minHeight: 400 }}>
      <div style={{ marginBottom: 24 }}>
        <h4 style={{ margin: '0 0 4px', fontSize: 18, fontWeight: 700, color: T.text }}>📋 Contracts & Signed Documents</h4>
        <p style={{ margin: 0, fontSize: 13, color: T.textMuted }}>Upload signed contracts and agreement documents.</p>
      </div>

      <div
        onClick={() => triggerFileInput('contracts')}
        style={{
          padding: 32, borderRadius: T.radius, border: `2px dashed ${T.border}`,
          background: T.inputBg, textAlign: 'center', cursor: 'pointer',
          transition: 'all 0.2s', marginBottom: 20,
        }}
      >
        <p style={{ margin: '0 0 4px', fontSize: 28 }}>📂</p>
        <p style={{ margin: 0, fontSize: 14, fontWeight: 600, color: T.textSecondary }}>Click to upload contracts</p>
        <p style={{ margin: '4px 0 0', fontSize: 12, color: T.textMuted }}>PDF, DOC, images accepted</p>
      </div>
      <input type="file" accept=".pdf,.doc,.docx,image/*" multiple ref={el => fileInputRefs.current.contracts = el} style={{ display: 'none' }} onChange={handleContractUpload} />

      {form.contracts.map((doc, i) => (
        <div key={i} style={{
          display: 'flex', gap: 14, alignItems: 'center', padding: 14,
          borderRadius: T.radius, marginBottom: 10, background: T.inputBg,
          border: `1px solid ${T.border}`,
        }}>
          <div style={{
            width: 44, height: 44, borderRadius: 10, background: T.primaryLight,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 20, flexShrink: 0,
          }}>📄</div>
          <div style={{ flex: 1 }}>
            <p style={{ margin: 0, fontSize: 13, fontWeight: 600, color: T.text }}>
              {doc.file?.name || `Contract ${i + 1}`}
            </p>
            <input
              style={{ ...inputStyle, fontSize: 12, padding: '6px 10px', marginTop: 6 }}
              placeholder="Description (optional)"
              value={doc.description}
              onChange={e => updateContractDesc(i, e.target.value)}
            />
          </div>
          <RemoveButton onClick={() => removeContract(i)} />
        </div>
      ))}
      {form.contracts.length === 0 && (
        <p style={{ textAlign: 'center', color: T.textMuted, fontSize: 13, padding: 20 }}>No contracts uploaded yet.</p>
      )}
    </div>
  );

  const renderStep8 = () => (
    <div style={{ background: T.card, borderRadius: T.radiusXl, padding: '28px 32px', boxShadow: T.shadowMd, border: `1px solid ${T.border}`, minHeight: 400 }}>
      <div style={{ marginBottom: 24 }}>
        <h4 style={{ margin: '0 0 4px', fontSize: 18, fontWeight: 700, color: T.text }}>✅ Review & Submit</h4>
        <p style={{ margin: 0, fontSize: 13, color: T.textMuted }}>Review all information before final submission.</p>
      </div>

      {[
        { step: 1, title: 'Personal Information', items: [
          { label: 'National ID', value: form.nationalId },
          { label: 'Date of Birth', value: form.dateOfBirth },
          { label: 'Gender', value: form.gender },
          { label: 'Occupation', value: form.occupation },
          { label: 'Monthly Income', value: form.monthlyIncome ? `KES ${Number(form.monthlyIncome).toLocaleString()}` : '-' },
        ]},
        { step: 1, title: 'Responsible Officers', items: [
          { label: 'Loan Officer', value: form.loanOfficerId ? ((availableOfficers.find(o => o._id === form.loanOfficerId)?.firstName || '') + ' ' + (availableOfficers.find(o => o._id === form.loanOfficerId)?.lastName || '')) : 'Automatic assignment' },
          { label: 'Collection Officer', value: form.collectionOfficerId ? ((availableOfficers.find(o => o._id === form.collectionOfficerId)?.firstName || '') + ' ' + (availableOfficers.find(o => o._id === form.collectionOfficerId)?.lastName || '')) : 'Automatic assignment' },
        ]},
        { step: 2, title: 'Home Address', items: [
          { label: 'Street', value: form.homeAddress.street || '-' },
          { label: 'City', value: form.homeAddress.city || '-' },
          { label: 'County', value: form.homeAddress.county || '-' },
          { label: 'GPS', value: form.homeGps ? `${form.homeGps.latitude.toFixed(4)}, ${form.homeGps.longitude.toFixed(4)}` : 'Not captured' },
        ]},
        { step: 3, title: 'Business Details', items: [
          { label: 'Business Name', value: form.businessName || '-' },
          { label: 'Type', value: form.businessType || '-' },
          { label: 'Sector', value: form.businessSector || '-' },
          { label: 'Address', value: form.businessAddress || '-' },
        ]},
        { step: 4, title: 'Collateral', items: form.collateral.filter(c => c.type).map((c, i) => ({
          label: `Item ${i + 1}`, value: `${c.type} — KES ${Number(c.estimatedValue || 0).toLocaleString()}`
        }))},
        { step: 5, title: 'KYC Documents', items: REQUIRED_KYC_DOCS.map(d => ({
          label: d.label,
          value: form.kycDocuments.find(k => k.documentType === d.key) ? '✅ Uploaded' : '❌ Missing',
        }))},
        { step: 6, title: 'Registration Fee', items: [
          { label: 'Phone', value: form.paymentPhone || '-' },
          { label: 'Method', value: form.paymentMethod === 'mpesa_code' ? 'M-Pesa Transaction Code' : 'M-Pesa Prompt' },
          { label: 'Transaction Code', value: form.paymentCode || '-' },
          { label: 'Status', value: form.paymentStatus === 'paid' ? '✅ Paid & Verified' : `⚠️ ${form.paymentStatus || 'Pending'}` },
          { label: 'Receipt', value: form.paymentReceipt || '-' },
        ]},
        { step: 7, title: 'Contracts', items: form.contracts.length > 0
          ? form.contracts.map((c, i) => ({ label: `Document ${i + 1}`, value: c.description || c.file?.name || 'Uploaded' }))
          : [{ label: 'Status', value: '⚠️ No contracts uploaded' }]
        },
      ].map(section => (
        <div key={section.step} style={{
          marginBottom: 16, borderRadius: T.radius, border: `1px solid ${T.border}`,
          overflow: 'hidden',
        }}>
          <div style={{
            padding: '10px 16px', background: T.inputBg,
            borderBottom: `1px solid ${T.border}`,
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          }}>
            <span style={{ fontSize: 13, fontWeight: 700, color: T.text }}>{section.title}</span>
            <span style={{
              fontSize: 11, fontWeight: 600, padding: '2px 10px', borderRadius: 10,
              background: completedSteps.has(section.step) ? T.successLight : T.warningLight,
              color: completedSteps.has(section.step) ? T.success : T.warning,
            }}>
              {completedSteps.has(section.step) ? '✓ Complete' : 'Incomplete'}
            </span>
          </div>
          <div style={{ padding: '10px 16px' }}>
            {section.items.map((item, i) => (
              <div key={i} style={{
                display: 'flex', justifyContent: 'space-between', padding: '5px 0',
                borderBottom: i < section.items.length - 1 ? `1px solid ${T.borderLight}` : 'none',
              }}>
                <span style={{ fontSize: 12, color: T.textSecondary }}>{item.label}</span>
                <span style={{ fontSize: 12, fontWeight: 500, color: T.text, textAlign: 'right', maxWidth: '60%' }}>{item.value}</span>
              </div>
            ))}
          </div>
        </div>
      ))}

      <div style={{
        marginTop: 20, padding: '16px 20px', borderRadius: T.radius,
        background: T.primaryLight, border: `1px solid ${T.primary}20`,
        display: 'flex', alignItems: 'center', gap: 12,
      }}>
        <span style={{ fontSize: 20 }}>⚠️</span>
        <p style={{ margin: 0, fontSize: 13, color: T.primaryDark, lineHeight: 1.5 }}>
          By submitting, you confirm that all information provided is accurate and complete. This action will finalize the client onboarding process.
        </p>
      </div>
    </div>
  );

  const renderCurrentStep = () => {
    switch (currentStep) {
      case 1: return renderStep1();
      case 2: return renderStep2();
      case 3: return renderStep3();
      case 4: return renderStep4();
      case 5: return renderStep5();
      case 6: return renderStep6();
      case 7: return renderStep7();
      case 8: return renderStep8();
      default: return null;
    }
  };

  // ═══════════════════════════════════════════════════════════════
  // MAIN RENDER
  // ═══════════════════════════════════════════════════════════════
  if (!draftLoaded) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: 48, height: 48, border: `3px solid ${T.border}`, borderTopColor: T.primary,
            borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '0 auto 16px',
          }} />
          <p style={{ color: T.textSecondary, fontSize: 14 }}>Loading onboarding data...</p>
        </div>
      </div>
    );
  }

  // FIX 2: Next button disabled on step 6 unless paid
  const isNextEnabled = () => {
    if (loading) return false;
    if (currentStep === 6 && form.paymentStatus !== 'paid') return false;
    return true;
  };

  const getNextLabel = () => {
    if (currentStep === 8) return loading ? 'Submitting...' : 'Submit Application';
    return loading ? 'Saving...' : 'Next';
  };

  return (
    <div style={{ maxWidth: 960, margin: '0 auto', padding: '24px 20px 60px' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
        <div>
          <button type="button" onClick={() => navigate('/leads')} style={{
            display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 14px',
            borderRadius: 8, border: `1px solid ${T.border}`, background: T.card,
            color: T.textSecondary, fontSize: 12, fontWeight: 500, cursor: 'pointer', marginBottom: 10,
          }}>← Back to Leads</button>
          <h3 style={{ margin: '0 0 2px', fontSize: 22, fontWeight: 800, color: T.text, letterSpacing: '-0.02em' }}>
            Client Onboarding
          </h3>
          <p style={{ margin: 0, fontSize: 13, color: T.textMuted }}>
            {lead?.firstName} {lead?.lastName} — {lead?.phone}
            {lastSaved && <span style={{ marginLeft: 12, color: T.success, fontWeight: 500 }}>✓ Saved {lastSaved.toLocaleTimeString()}</span>}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button type="button" onClick={() => setInteractionPanelOpen(true)} style={{
            padding: '8px 16px', borderRadius: 10, border: `1px solid ${T.border}`,
            background: T.card, color: T.textSecondary, fontSize: 12, fontWeight: 600,
            cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6,
          }}>📋 Notes ({interactions.length})</button>
          <button type="button" onClick={() => setInteractionModalOpen(true)} style={{
            padding: '8px 16px', borderRadius: 10, border: 'none',
            background: `linear-gradient(135deg, ${T.primary} 0%, ${T.primaryDark} 100%)`,
            color: '#fff', fontSize: 12, fontWeight: 600, cursor: 'pointer',
            boxShadow: `0 2px 8px ${T.primary}30`, display: 'flex', alignItems: 'center', gap: 6,
          }}>+ Add Note</button>
        </div>
      </div>

      {/* Step Indicator */}
      <StepIndicator steps={STEPS} currentStep={currentStep} completedSteps={completedSteps} />

      {/* Error */}
      {error && (
        <div style={{
          padding: '14px 18px', borderRadius: T.radius, marginBottom: 16,
          background: T.dangerLight, border: `1px solid ${T.dangerBorder}`,
          color: '#991b1b', fontSize: 13, fontWeight: 500,
          display: 'flex', alignItems: 'center', gap: 10,
        }}>
          <span style={{ fontSize: 18 }}>⚠️</span>
          <span style={{ flex: 1 }}>{error}</span>
          <button type="button" onClick={() => setError('')} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 16, color: '#991b1b', padding: '0 4px' }}>✕</button>
        </div>
      )}

      {/* Step Message */}
      {stepMessage && (
        <div style={{
          padding: '14px 18px', borderRadius: T.radius, marginBottom: 16,
          background: T.successLight, border: `1px solid ${T.successBorder}`,
          color: '#065f46', fontSize: 13, fontWeight: 500,
          display: 'flex', alignItems: 'center', gap: 10,
        }}>
          <span>{stepMessage}</span>
        </div>
      )}

      {/* Step Content */}
      {renderCurrentStep()}

      {/* Navigation */}
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        marginTop: 24, padding: '18px 24px', borderRadius: T.radiusXl,
        background: T.card, border: `1px solid ${T.border}`, boxShadow: T.shadowMd,
      }}>
        <button
          type="button"
          onClick={handleBack}
          disabled={currentStep === 1}
          style={{
            padding: '11px 28px', borderRadius: 10,
            border: `1.5px solid ${currentStep === 1 ? T.border : T.primary}`,
            background: currentStep === 1 ? T.inputBg : T.card,
            color: currentStep === 1 ? T.textMuted : T.primary,
            fontSize: 14, fontWeight: 600,
            cursor: currentStep === 1 ? 'not-allowed' : 'pointer',
            transition: 'all 0.15s', display: 'flex', alignItems: 'center', gap: 6,
          }}
        >
          ← Back
        </button>
        <span style={{ fontSize: 12, color: T.textMuted, fontWeight: 500 }}>
          Step {currentStep} of {STEPS.length}
        </span>
        <button
          type="button"
          onClick={handleNext}
          disabled={!isNextEnabled()}
          style={{
            padding: '11px 28px', borderRadius: 10, border: 'none',
            background: isNextEnabled()
              ? currentStep === 8
                ? `linear-gradient(135deg, ${T.success} 0%, #047857 100%)`
                : `linear-gradient(135deg, ${T.primary} 0%, ${T.primaryDark} 100%)`
              : '#c7d2fe',
            color: '#fff',
            fontSize: 14, fontWeight: 700,
            cursor: isNextEnabled() ? 'pointer' : 'not-allowed',
            boxShadow: isNextEnabled()
              ? currentStep === 8
                ? `0 4px 16px ${T.success}35`
                : `0 4px 16px ${T.primary}30`
              : 'none',
            transition: 'all 0.2s', display: 'flex', alignItems: 'center', gap: 6,
          }}
        >
          {getNextLabel()}
          {currentStep !== 8 && '→'}
        </button>
      </div>

      {/* Modals */}
      <InteractionModal
        isOpen={interactionModalOpen}
        onClose={() => setInteractionModalOpen(false)}
        onSave={handleSaveInteraction}
        stepId={currentStep}
        stepTitle={STEPS.find(s => s.id === currentStep)?.title}
        loading={savingInteraction}
      />
      <InteractionPanel
        isOpen={interactionPanelOpen}
        onClose={() => setInteractionPanelOpen(false)}
        interactions={interactions}
        loading={interactionsLoading}
      />

      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
        input:focus, select:focus, textarea:focus {
          border-color: ${T.primary} !important;
          box-shadow: 0 0 0 3px ${T.primary}18 !important;
        }
      `}</style>
    </div>
  );
}